import * as React from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import {
  CrossCheckClause,
  CrossCheckMatter,
  CrossCheckInsight,
  BankOfChangesMulti,
  BankOfChangesOption,
  BankOfChangesValue,
  ClauseEditHistoryEntry,
  PastAgreementOption,
  getPrimaryBankOfChangesText,
  getBocOptionCount,
  formatClauseNumberForDisplay,
} from '../../types/crossCheck';
import { detectTextDirection } from '../../utils/textUtils';
import { filterPastAgreementOptions, type PastNegotiationTimeFilter } from '../../utils/clauseEditorFilters';
import { TrackChangesParser } from '../../word/utils/TrackChangesParser';
import { RobustXmlTrackChangesGenerator } from '../../features/rephrasing/RobustXmlTrackChangesGenerator';
import {
  RedlineInserter,
  type ApplyParagraphResult,
  type SubmitRevertContext,
} from '../../word/trackChanges/RedlineInserter';
import { submitRevertContextFromSubmissionStatus } from '../../utils/submitRevertContext';
import { debugLog, debugWarn } from '../../utils/RemoteDebugLog';
import { isLegacyApplyEnabled, subscribeLegacyApplyEnabled } from '../../utils/addInPreferences';
import { applyViaHybridEngine } from '../../features/hybridApply/hybridEngine';
import { previewHybridOps } from '../../features/hybridApply/previewHybrid';

/** Hybrid engine is the default apply path; the Profile "Legacy apply" toggle opts back to legacy. */
const isHybridApplyEnabled = (): boolean => !isLegacyApplyEnabled();
const subscribeHybridApplyEnabled = subscribeLegacyApplyEnabled;
import { DocumentReader } from '../../word/api/DocumentReader';
import { getClientRequestSearchText, getTableRowSearchAnchors, getModuleBNavigationAnchor, isTableRowClause, usesOoxmlParagraphIndex, getDisplayTrackChangesParts, resolveEffectiveParagraphIndex, type CrossCheckFeatureContext } from '../../utils/crossCheckDocumentUtils';
import type { CrossCheckSolution } from '../../utils/crossCheckSolution';
import { crossCheckSolutionFromKebabVariant } from '../../utils/crossCheckSolution';
import apiService from '../../services/api';
import { handleError } from '../../utils/errorHandling';
import { CostLimitExceededError } from '../../utils/usageLimitError';
import { PastNegotiationSourceModal } from './PastNegotiationSourceModal';
import './CrossCheckClauseCard.css';

/** Human-readable labels for the Client Request source (Module B preference). */
const CLIENT_REQUEST_SOURCE_LABELS: Record<string, string> = {
  their_side_redline: 'Their side',
  our_side_redline: 'Our side',
  plain_text: 'Plain text',
};

interface CrossCheckClauseCardProps {
  clause: CrossCheckClause;
  index: number;
  projectId?: string;
  subProjectId?: string;
  onToggleLock?: (clause: CrossCheckClause) => void;
  isUpdatingLock?: boolean;
  onResponseSubmitted?: (clauseId: string, revertContext?: SubmitRevertContext) => void;
  onResponseUndone?: (clauseId: string) => void;
  submitRevertContextForClause?: SubmitRevertContext;
  /** Batched from CrossCheckResultsView — avoids N parallel playbook HTTP calls per page load. */
  pastAgreementPlaybookXmlById?: Record<string, string>;
  pastAgreementPlaybookSideById?: Record<string, 'our_side' | 'their_side'>;
  /** Resolved from playbook record → files table (batch API). */
  pastAgreementPlaybookFileNameById?: Record<string, string>;
  pastAgreementPlaybookFileIdById?: Record<string, string>;
  /** Fallback: playbook id -> final_version source when option.is_final_version is absent (legacy results). */
  pastAgreementPlaybookFinalVersionById?: Record<string, boolean>;
  /** Plain paragraph_text resolved from the playbook by-ids batch API (replaces persisted opt.text). */
  pastAgreementPlaybookPlainTextById?: Record<string, string>;
  /** Playbook id -> negotiation author (batch API). */
  pastAgreementPlaybookAuthorById?: Record<string, string>;
  /** When null, show all past negotiation authors; otherwise only matching options. */
  pastAgreementAuthorFilter?: Set<string> | null;
  /** Playbook id -> negotiation timestamp (batch API). */
  pastAgreementPlaybookTimestampById?: Record<string, string>;
  /** When null, show all times; otherwise filter past negotiations by timestamp. */
  pastAgreementTimeFilter?: PastNegotiationTimeFilter;
  pastAgreementPlaybooksLoading?: boolean;
  pastAgreementDomain?: 'construction-loan' | 'real-estate';
  /**
   * Called when the user clicks the "Go to" badge in the Sales Agreement section.
   * Implemented by the parent so the PDF preview can be shown as a sticky split
   * panel above all cards (not as an in-card modal). When omitted the badge is
   * hidden — this matches the dashboard's "no source PDF available" state.
   */
  onGoToSalesAgreementSource?: (clause: CrossCheckClause, searchText?: string) => void;
  /** True when this card's clause is the one currently shown in the split panel. */
  isSalesAgreementSourceActive?: boolean;
  /** True while we're loading the SA PDF for this clause specifically. */
  isSalesAgreementSourceLoading?: boolean;
  onMoveClause?: (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => void;
  /** Construction Loan: move between Commercial / Legal / Other (no re-analysis). */
  onMoveClauseMatter?: (clause: CrossCheckClause, target: CrossCheckMatter) => void;
  onAnalyzeAgain?: (clause: CrossCheckClause) => void;
  /** Construction Loan: open playbook agent chat in split view. */
  onAskAgent?: (clause: CrossCheckClause) => void;
  /** True while a move/reanalyze request is in flight for this clause. */
  isMutating?: boolean;
  /** MCC document-type menu vs Construction Loan matter menu. */
  kebabVariant?: 'mcc' | 'construction-loan';
  /** Which cross-check solution is active — gates MCC-only table-row TC behaviour. */
  crossCheckSolution?: CrossCheckSolution;
  /** Ask Agent: streaming AI suggestion onto this card's client request. */
  externalAiStreaming?: boolean;
  /** Ask Agent: injected track-changes XML after chat-based suggestion. */
  externalEditedXml?: string | null;
  /** Ref on the card header row — used to scroll into view when Ask Agent opens. */
  cardHeaderRef?: React.Ref<HTMLDivElement>;
  /** Called after a BoC (or other) edit is saved so the parent can refresh clause state. */
  onClauseEdited?: (clause: CrossCheckClause) => void;
  /** In-sub-project success/error banner (dashboard toast equivalent). */
  onActionBanner?: (banner: { type: 'success' | 'error'; message: string }) => void;
  /** Refresh BrainShare pending badge after sharing to BrainShare. */
  onBrainShareUpdated?: () => void;
}

const getDir = (text: string | undefined): 'rtl' | 'ltr' => {
  if (!text) return 'ltr';
  return detectTextDirection(text);
};

function getInsightExistingClauseParagraphIndex(insight: CrossCheckInsight): number | null {
  if (insight.paragraph_index != null && !Number.isNaN(Number(insight.paragraph_index))) {
    return Number(insight.paragraph_index);
  }
  const cm = insight.chunk_metadata?.paragraph_index;
  if (cm != null && !Number.isNaN(Number(cm))) {
    return Number(cm);
  }
  return null;
}

function getInsightExistingClauseSearchText(insight: CrossCheckInsight): string {
  const t = insight.exist_clause?.trim();
  if (t) return t;
  const xml = insight.exist_clause_xml?.trim();
  if (xml) {
    try {
      const accepted = TrackChangesParser.extractAcceptedText(xml).trim();
      if (accepted) return accepted;
      return TrackChangesParser.extractText(xml).trim();
    } catch {
      return '';
    }
  }
  return '';
}

function canNavigateToInsightExistingClause(insight: CrossCheckInsight): boolean {
  return (
    getInsightExistingClauseParagraphIndex(insight) != null ||
    getInsightExistingClauseSearchText(insight).length > 0
  );
}

/** Prefer the sole insight; otherwise the first with text usable for SA PDF go-to. */
function pickInsightForSalesAgreementGoTo(
  insights: CrossCheckInsight[],
): CrossCheckInsight | null {
  if (!insights.length) return null;
  if (insights.length === 1) return insights[0];
  return insights.find((ins) => getInsightExistingClauseSearchText(ins).length > 0) ?? null;
}

function reportClauseAiError(
  err: unknown,
  onActionBanner: CrossCheckClauseCardProps['onActionBanner'],
  context: string,
): void {
  console.error(`[CrossCheckClauseCard] ${context}:`, err);
  if (err instanceof CostLimitExceededError) return;
  onActionBanner?.({ type: 'error', message: handleError(err) });
}

/** Extract first suggestedResponse from partial streaming JSON (dashboard ClauseCard parity). */
function extractSuggestedResponse(jsonText: string): string {
  try {
    const startIndex = jsonText.indexOf('"suggestedResponse"');
    if (startIndex === -1) return '';
    const colonIndex = jsonText.indexOf(':', startIndex);
    if (colonIndex === -1) return '';
    const quoteStartIndex = jsonText.indexOf('"', colonIndex);
    if (quoteStartIndex === -1) return '';

    let result = '';
    let i = quoteStartIndex + 1;
    while (i < jsonText.length) {
      const char = jsonText[i];
      if (char === '\\') {
        if (i + 1 < jsonText.length) {
          const nextChar = jsonText[i + 1];
          if (nextChar === 'n') {
            result += '\n';
            i += 2;
          } else if (nextChar === '"') {
            result += '"';
            i += 2;
          } else if (nextChar === '\\') {
            result += '\\';
            i += 2;
          } else {
            result += char;
            i++;
          }
        } else {
          result += char;
          i++;
        }
      } else if (char === '"') {
        break;
      } else {
        result += char;
        i++;
      }
    }
    return result;
  } catch {
    return '';
  }
}

/** Extract all suggestedResponse values from partial streaming JSON (multi-insight modal). */
function extractAllSuggestedResponses(jsonText: string): string[] {
  const results: string[] = [];
  const key = `"suggestedResponse": "`;
  let searchIndex = 0;
  while (true) {
    const startIndex = jsonText.indexOf(key, searchIndex);
    if (startIndex === -1) break;

    let result = '';
    let i = startIndex + key.length;
    let inString = true;
    while (i < jsonText.length && inString) {
      const char = jsonText[i];
      if (char === '\\') {
        if (i + 1 < jsonText.length) {
          const nextChar = jsonText[i + 1];
          if (nextChar === 'n') {
            result += '\n';
            i += 2;
          } else if (nextChar === '"') {
            result += '"';
            i += 2;
          } else if (nextChar === '\\') {
            result += '\\';
            i += 2;
          } else {
            result += char;
            i++;
          }
        } else {
          result += char;
          i++;
        }
      } else if (char === '"') {
        inString = false;
        break;
      } else {
        result += char;
        i++;
      }
    }
    if (result.length > 0) results.push(result);
    searchIndex = startIndex + key.length;
  }
  return results;
}

function formatInsightsForApi(
  insights: CrossCheckInsight[],
  moduleType?: string,
): Array<Record<string, unknown>> {
  const isContracts = moduleType === 'module_b';
  const isXmlFormat = isContracts && insights.some((insight) => insight.exist_clause_xml);
  return insights.map((insight) => {
    if (isXmlFormat) {
      return {
        exist_clause_xml: insight.exist_clause_xml || insight.exist_clause,
        explanation: insight.explanation,
        file_id: insight.file_id,
        paragraph_index: insight.paragraph_index,
        original_xml: insight.original_xml,
        edited_xml: insight.edited_xml,
      };
    }
    return {
      exist_clause: insight.exist_clause || insight.exist_clause_xml,
      explanation: insight.explanation,
    };
  });
}

interface InsightAiSuggestion {
  clauseReference: string;
  explanation: string;
  suggestedResponse: string | null;
}

function buildAiSuggestionPreviewXml(
  clientRequestXml: string | null,
  clientRequestPlainText: string,
  suggestedText: string,
): string | null {
  const trimmed = suggestedText.trim();
  if (!trimmed) return null;
  if (
    trimmed.startsWith('<w:') ||
    trimmed.startsWith('<pkg:') ||
    trimmed.startsWith('<?xml') ||
    trimmed.includes('<w:p')
  ) {
    return trimmed;
  }
  const crXml =
    clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
  try {
    return RobustXmlTrackChangesGenerator.generateTrackChangesXml(crXml, trimmed, 'AI Suggestion');
  } catch {
    return null;
  }
}

function convertClientRequestXmlToHtml(xml: string): string | null {
  try {
    return TrackChangesParser.convertOoxmlToHtml(xml);
  } catch {
    return null;
  }
}

function isInsideDeletionNode(node: Node, root: HTMLElement): boolean {
  let current: Node | null = node;
  while (current && current !== root) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const el = current as Element;
      const tag = el.tagName.toLowerCase();
      if (tag === 'del' || el.classList.contains('track-change-deletion')) {
        return true;
      }
    }
    current = current.parentNode;
  }
  return false;
}

/** Accepted paragraph text from track-changes HTML — excludes <del> content. */
function extractAcceptedTextFromHtmlElement(container: HTMLElement): string {
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let text = '';
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (!isInsideDeletionNode(node, container)) {
      text += node.textContent || '';
    }
  }
  return text;
}

/** Caret offset in accepted-text coordinates (skips <del> text). */
function saveAcceptedCaretOffset(container: HTMLElement): number {
  const sel = window.getSelection();
  if (!sel?.rangeCount) return 0;
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) return 0;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let offset = 0;
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (isInsideDeletionNode(node, container)) continue;
    if (node === range.startContainer) {
      return offset + range.startOffset;
    }
    offset += node.textContent?.length ?? 0;
  }
  return offset;
}

function restoreAcceptedCaretOffset(container: HTMLElement, offset: number): void {
  const sel = window.getSelection();
  if (!sel) return;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, null);
  let remaining = Math.max(0, offset);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (isInsideDeletionNode(node, container)) continue;
    const len = node.textContent?.length ?? 0;
    if (remaining <= len) {
      const range = document.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
      return;
    }
    remaining -= len;
  }

  const range = document.createRange();
  range.selectNodeContents(container);
  range.collapse(false);
  sel.removeAllRanges();
  sel.addRange(range);
}

function getAcceptedOffsetAtPoint(container: HTMLElement, node: Node, offset: number): number {
  const range = document.createRange();
  range.selectNodeContents(container);
  try {
    range.setEnd(node, offset);
  } catch {
    return saveAcceptedCaretOffset(container);
  }
  const fragment = range.cloneContents();
  const temp = document.createElement('div');
  temp.appendChild(fragment);
  return extractAcceptedTextFromHtmlElement(temp).length;
}

function saveAcceptedSelectionRange(container: HTMLElement): { start: number; end: number } {
  const sel = window.getSelection();
  if (!sel?.rangeCount) {
    const offset = saveAcceptedCaretOffset(container);
    return { start: offset, end: offset };
  }
  const range = sel.getRangeAt(0);
  if (!container.contains(range.commonAncestorContainer)) {
    const offset = saveAcceptedCaretOffset(container);
    return { start: offset, end: offset };
  }
  const start = getAcceptedOffsetAtPoint(container, range.startContainer, range.startOffset);
  const end = getAcceptedOffsetAtPoint(container, range.endContainer, range.endOffset);
  return start <= end ? { start, end } : { start: end, end: start };
}

/** Snapshot for track-changes edit undo/redo (browser undo is cleared on each innerHTML refresh). */
type CrEditHistorySnapshot = {
  text: string;
  previewXml: string | null;
  caret: number;
};

const CR_EDIT_UNDO_LIMIT = 100;

function isTrackChangesHistoryInputType(inputType: string): boolean {
  return inputType === 'historyUndo' || inputType === 'historyRedo';
}

function isTrackChangesInsertInputType(inputType: string): boolean {
  return (
    inputType === 'insertText' ||
    inputType === 'insertReplacementText' ||
    inputType === 'insertFromPaste' ||
    inputType === 'insertFromDrop' ||
    inputType === 'insertParagraph' ||
    inputType === 'insertLineBreak'
  );
}

/**
 * Office WebView often omits inputType/data on Space and other keys.
 * Pair keydown → beforeinput using the pending key captured on keydown.
 */
function inferTrackChangesBeforeInput(
  inputEvent: InputEvent,
  pendingKey: string | null,
): { inputType: string; data: string | null } {
  let inputType = inputEvent.inputType || '';
  let data: string | null = inputEvent.data;

  if (!inputType && pendingKey === 'Backspace') {
    inputType = 'deleteContentBackward';
  } else if (!inputType && pendingKey === 'Delete') {
    inputType = 'deleteContentForward';
  } else if (
    pendingKey != null &&
    pendingKey.length === 1 &&
    pendingKey !== '\n' &&
    (!inputType || inputType === 'insertText') &&
    (data == null || data === '')
  ) {
    inputType = inputType || 'insertText';
    data = pendingKey;
  }

  return { inputType, data };
}

/**
 * Prefer the caret we restored after the last sync refresh. DOM selection in
 * contentEditable track-changes HTML often drifts by a frame (system beep).
 */
function getTrackChangesEditSelection(
  container: HTMLElement,
  expectedCaret: number,
): { start: number; end: number } {
  const raw = saveAcceptedSelectionRange(container);
  if (raw.start !== raw.end) return raw;
  const delta = Math.abs(raw.start - expectedCaret);
  if (delta > 1) return raw;
  return { start: expectedCaret, end: expectedCaret };
}

/**
 * Map DOM selection to model coordinates for an input event.
 * When the browser already applied an insertion, the collapsed caret in `input`
 * sits after the new text — subtract data.length to get the pre-insert offset.
 */
function isTrackChangesDeleteInputType(inputType: string): boolean {
  return (
    inputType === 'deleteContentBackward' ||
    inputType === 'deleteContentForward' ||
    inputType === 'deleteWordBackward' ||
    inputType === 'deleteWordForward' ||
    inputType === 'deleteByCut'
  );
}

/**
 * Accepted text in DOM coordinates — must match `saveAcceptedSelectionRange`.
 *
 * NOTE: `TrackChangesParser.extractAcceptedText` collapses consecutive whitespace
 * (REF noise normalization), so its length differs from the DOM / position map.
 * The DOM walker preserves all spaces so selection indices align.
 */
function getTrackChangesEditCurrentText(
  container: HTMLElement | null,
  fallback: string,
): string {
  if (!container) return fallback;
  try {
    return extractAcceptedTextFromHtmlElement(container);
  } catch {
    return fallback;
  }
}

function resolveTrackChangesInputSelection(
  container: HTMLElement,
  inputType: string,
  inputData: string | null,
  expectedCaret: number,
  browserAlreadyApplied: boolean,
  pendingSelection: { start: number; end: number } | null = null,
): { start: number; end: number; usedExpectedCaret: boolean } {
  if (
    pendingSelection &&
    pendingSelection.start !== pendingSelection.end &&
    (isTrackChangesDeleteInputType(inputType) || isTrackChangesInsertInputType(inputType))
  ) {
    return {
      start: pendingSelection.start,
      end: pendingSelection.end,
      usedExpectedCaret: false,
    };
  }

  const raw = saveAcceptedSelectionRange(container);
  if (raw.start !== raw.end) {
    return { start: raw.start, end: raw.end, usedExpectedCaret: false };
  }

  const insertLen =
    browserAlreadyApplied &&
    isTrackChangesInsertInputType(inputType) &&
    inputData != null &&
    inputData.length > 0
      ? inputData.length
      : 0;

  if (insertLen > 0) {
    const domBefore = Math.max(0, raw.start - insertLen);
    // Office WebView often leaves the caret at the previous typing position after a click.
    if (Math.abs(domBefore - expectedCaret) > 1) {
      return {
        start: expectedCaret,
        end: expectedCaret,
        usedExpectedCaret: true,
      };
    }
    return { start: domBefore, end: domBefore, usedExpectedCaret: false };
  }

  const trusted = getTrackChangesEditSelection(container, expectedCaret);
  return {
    start: trusted.start,
    end: trusted.end,
    usedExpectedCaret: trusted.start === expectedCaret,
  };
}

function isTrackChangesClipboardInputType(inputType: string): boolean {
  return (
    inputType === 'insertFromPaste' ||
    inputType === 'insertFromDrop' ||
    inputType === 'deleteByCut'
  );
}

function applyTrackChangesEditInput(
  el: HTMLElement,
  inputType: string,
  inputData: string | null,
  currentValue: string,
  expectedCaret: number,
  browserAlreadyApplied = false,
  pendingSelection: { start: number; end: number } | null = null,
): { text: string; caret: number } | null {
  const selection = resolveTrackChangesInputSelection(
    el,
    inputType,
    inputData,
    expectedCaret,
    browserAlreadyApplied,
    pendingSelection,
  );
  return applyInputEventToAcceptedText(currentValue, selection, inputType, inputData);
}

function applyInputEventToAcceptedText(
  text: string,
  range: { start: number; end: number },
  inputType: string,
  data: string | null,
): { text: string; caret: number } | null {
  const { start, end } = range;
  const hasSelection = end > start;

  switch (inputType) {
    case 'insertText':
    case 'insertReplacementText':
    case 'insertFromPaste':
    case 'insertFromDrop': {
      const insertData = data ?? '';
      const next = text.slice(0, start) + insertData + text.slice(end);
      return { text: next, caret: start + insertData.length };
    }
    case 'deleteContentBackward':
    case 'deleteWordBackward': {
      if (hasSelection) {
        return { text: text.slice(0, start) + text.slice(end), caret: start };
      }
      if (start > 0) {
        return { text: text.slice(0, start - 1) + text.slice(start), caret: start - 1 };
      }
      return { text, caret: start };
    }
    case 'deleteContentForward':
    case 'deleteWordForward': {
      if (hasSelection) {
        return { text: text.slice(0, start) + text.slice(end), caret: start };
      }
      if (start < text.length) {
        return { text: text.slice(0, start) + text.slice(start + 1), caret: start };
      }
      return { text, caret: start };
    }
    case 'deleteByCut': {
      if (!hasSelection) return null;
      return { text: text.slice(0, start) + text.slice(end), caret: start };
    }
    case 'insertParagraph':
    case 'insertLineBreak': {
      const next = text.slice(0, start) + '\n' + text.slice(end);
      return { text: next, caret: start + 1 };
    }
    default: {
      // Office WebView sometimes omits inputType; infer from data / selection.
      if (data != null && data !== '') {
        const next = text.slice(0, start) + data + text.slice(end);
        return { text: next, caret: start + data.length };
      }
      if (hasSelection) {
        return { text: text.slice(0, start) + text.slice(end), caret: start };
      }
      return null;
    }
  }
}

interface ClientRequestXmlPreviewProps {
  xml?: string | null | undefined;
  html?: string | null;
  dir?: 'rtl' | 'ltr';
  className?: string;
}

/** Same track-changes HTML preview used in the Client Request box on the clause card. */
function ClientRequestXmlPreview({ xml, html, dir = 'ltr', className }: ClientRequestXmlPreviewProps) {
  const renderedHtml = React.useMemo(
    () => html ?? (xml ? convertClientRequestXmlToHtml(xml) : null),
    [html, xml],
  );
  if (!renderedHtml) return null;
  return (
    <div className={`cc-paragraph-xml-display${className ? ` ${className}` : ''}`} dir={dir}>
      <div
        className="cc-selected-text-xml"
        dangerouslySetInnerHTML={{ __html: renderedHtml }}
        dir={dir}
      />
    </div>
  );
}

interface InsightSuggestionPreviewProps {
  suggestedText: string;
  clientRequestXml: string | null;
  clientRequestPlainText: string;
}

/** Preview an insight AI suggestion with the same XML renderer as the clause card. */
function InsightSuggestionPreview({
  suggestedText,
  clientRequestXml,
  clientRequestPlainText,
}: InsightSuggestionPreviewProps) {
  const previewXml = React.useMemo(
    () => buildAiSuggestionPreviewXml(clientRequestXml, clientRequestPlainText, suggestedText),
    [clientRequestXml, clientRequestPlainText, suggestedText],
  );
  const dir = React.useMemo((): 'rtl' | 'ltr' => {
    if (previewXml) {
      try {
        return getDir(TrackChangesParser.extractText(previewXml)) as 'rtl' | 'ltr';
      } catch {
        // fall through
      }
    }
    return getDir(suggestedText) as 'rtl' | 'ltr';
  }, [previewXml, suggestedText]);

  if (previewXml) {
    return <ClientRequestXmlPreview xml={previewXml} dir={dir} />;
  }
  return (
    <p className="cc-section-text" dir={dir}>
      {suggestedText}
    </p>
  );
}

/** Lightbulb icon used in insights modal (matches dashboard). */
const InsightsLightbulbIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
    <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
  </svg>
);

interface SalesAgreementGoToBadgeProps {
  onClick: () => void;
  disabled?: boolean;
  isActive?: boolean;
  isLoading?: boolean;
  className?: string;
  title?: string;
}

/** Shared "Go to" badge — Sales Agreement section and insights modal. */
const SalesAgreementGoToBadge: React.FC<SalesAgreementGoToBadgeProps> = ({
  onClick,
  disabled = false,
  isActive = false,
  isLoading = false,
  className = '',
  title,
}) => (
  <button
    type="button"
    className={`cc-sa-go-to-badge${isActive ? ' cc-sa-go-to-active' : ''}${className ? ` ${className}` : ''}`}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
    disabled={disabled || isLoading}
    title={
      title ??
      (isActive
        ? 'Close Sales Agreement PDF view'
        : 'Open Sales Agreement PDF and jump to this clause')
    }
    aria-pressed={isActive}
  >
    {isLoading ? (
      <>
        <span>Loading…</span>
        <span className="cc-sa-go-to-spinner" aria-hidden />
      </>
    ) : isActive ? (
      <span>Close</span>
    ) : (
      <>
        <svg className="cc-sa-go-to-badge-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
          <path
            fillRule="evenodd"
            d="M10.293 3.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L13.586 10H4a1 1 0 110-2h9.586l-3.293-3.293a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
        <span>Go to</span>
      </>
    )}
  </button>
);

function getStatusBadge(clause: CrossCheckClause): { label: string; className: string } {
  const hasClient = Boolean(clause.client_request?.trim());
  const hasSales = Boolean(clause.sales_agreement?.trim());
  const hasBoc = Boolean(getPrimaryBankOfChangesText(clause.bank_of_changes));

  if (clause.documentType === 'saved_for_later') {
    return { label: 'SAVED FOR LATER', className: 'cc-saved' };
  }
  if (hasClient && hasSales && hasBoc) {
    return { label: 'CLIENT REQUEST IDENTIFIED', className: 'cc-identified' };
  }
  if (hasClient && (hasSales || hasBoc)) {
    return { label: 'PARTIALLY MATCHED', className: 'cc-matched' };
  }
  if (hasClient) {
    return { label: 'CLIENT REQUEST IDENTIFIED', className: 'cc-identified' };
  }
  return { label: 'MISSING DATA', className: 'cc-missing' };
}

function getClientRequestXml(clause: CrossCheckClause): string | null {
  const parts = clause.track_changes?.track_changes_parts;
  if (Array.isArray(parts) && parts.length > 0) {
    const requestPart = parts.find(
      (part) => part && typeof part === 'object' && part.role === 'request' && part.paragraph_xml,
    );
    if (requestPart?.paragraph_xml) return requestPart.paragraph_xml;
    const firstXml = parts.find(
      (part) => part && typeof part === 'object' && typeof part.paragraph_xml === 'string' && part.paragraph_xml.trim(),
    );
    if (firstXml?.paragraph_xml) return firstXml.paragraph_xml;
  }
  const cr = clause.client_request?.trim() || '';
  if (cr && (
    cr.startsWith('<w:') ||
    cr.startsWith('<pkg:') ||
    cr.startsWith('<?xml') ||
    cr.includes('<w:p') ||
    cr.includes('<p ')
  )) {
    return cr;
  }
  if (clause.track_changes?.paragraph_xml) {
    return clause.track_changes.paragraph_xml;
  }
  return null;
}

function getClientRequestPlainText(clause: CrossCheckClause, xml: string | null): string {
  if (xml) {
    // Use accepted text (exclude w:del, include w:ins) so plain string matches what the
    // client intends after their track changes — not raw textContent which still embeds
    // deleted words and confuses the AI suggestion endpoint when comparing to BoC.
    try {
      return TrackChangesParser.extractAcceptedText(xml).replace(/\s+/g, ' ').trim();
    } catch { /* fallthrough */ }
  }
  if (clause.module_type === 'module_b' && clause.client_request) {
    try {
      const parsed = JSON.parse(clause.client_request);
      return parsed.edited_text || parsed.original_text || clause.client_request;
    } catch {
      // Plain-text Module B requests have no track changes, so the Word add-in uses this
      // string as the apply baseline — it must match the live document paragraph verbatim.
      // Cross-check formats client_request as "בסעיף {clause_number} - {request_text}", but
      // that template prefix is absent from the actual paragraph and desyncs the baseline,
      // which makes insertOoxml / incremental apply fail. original_text holds the raw
      // paragraph text (no prefix); when client_request is just a prefixed variant of it,
      // fall back to original_text so the baseline lines up with the live paragraph.
      const original = clause.original_text?.trim();
      if (original) {
        const crNorm = clause.client_request.replace(/\s+/g, ' ').trim();
        const origNorm = original.replace(/\s+/g, ' ').trim();
        if (origNorm && crNorm !== origNorm && crNorm.endsWith(origNorm)) {
          return original;
        }
      }
      return clause.client_request;
    }
  }
  return clause.client_request || '';
}

function getBocOptions(clause: CrossCheckClause): BankOfChangesOption[] | null {
  const boc = clause.bank_of_changes;
  if (!boc) return null;
  if (typeof boc === 'object' && 'options' in boc) {
    return (boc as BankOfChangesMulti).options;
  }
  if (Array.isArray(boc) && boc.length > 0) {
    return boc as BankOfChangesOption[];
  }
  return null;
}

function getBocCurrentTextForHistory(boc: BankOfChangesValue | undefined): string {
  if (!boc) return '';
  if (typeof boc === 'string') return boc;
  if ('chosen_option' in boc) return boc.chosen_option || '';
  if (Array.isArray(boc) && boc.length > 0) return boc[0]?.text || '';
  return '';
}

function applyBocEditLocalUpdate(
  clause: CrossCheckClause,
  newValue: string,
  isNewOption: boolean,
  selectedOptionIndex = 0,
): CrossCheckClause {
  const nowIso = new Date().toISOString();
  const currentHistory = clause.edit_history || {};
  const fieldHistory = Array.isArray(currentHistory.bank_of_changes)
    ? currentHistory.bank_of_changes
    : [];
  const current = clause.bank_of_changes;
  const currentTextValue = getBocCurrentTextForHistory(current);
  const appendedHistory: ClauseEditHistoryEntry[] = [
    ...fieldHistory,
    {
      original_text: isNewOption ? '' : currentTextValue,
      edited_text: newValue,
      edited_at: nowIso,
      edited_by: 'current-user',
      edited_by_name: 'You',
    },
  ];

  let bank_of_changes: BankOfChangesValue;
  if (isNewOption) {
    const newOption: BankOfChangesOption = {
      text: newValue,
      score: 1.0,
      rank: 1,
      original_extracted: newValue,
      method: 'manual_edit',
    };
    if (current && typeof current === 'object' && 'options' in current) {
      const options = [...(current as BankOfChangesMulti).options, {
        ...newOption,
        rank: (current as BankOfChangesMulti).options.length + 1,
      }];
      bank_of_changes = { chosen_option: newValue, options };
    } else if (Array.isArray(current)) {
      const options = [...current, { ...newOption, rank: current.length + 1 }];
      bank_of_changes = { chosen_option: newValue, options };
    } else {
      bank_of_changes = { chosen_option: newValue, options: [newOption] };
    }
  } else {
    const idx = selectedOptionIndex;
    if (current && typeof current === 'object' && 'options' in current) {
      const multi = current as BankOfChangesMulti;
      const options = [...(multi.options || [])];
      if (options[idx]) {
        options[idx] = { ...options[idx], text: newValue };
      }
      bank_of_changes = { chosen_option: newValue, options };
    } else if (Array.isArray(current)) {
      const options = [...current];
      if (options[idx]) {
        options[idx] = { ...options[idx], text: newValue };
      }
      bank_of_changes = { chosen_option: newValue, options };
    } else {
      bank_of_changes = {
        chosen_option: newValue,
        options: [{
          text: newValue,
          score: 1.0,
          rank: 1,
          original_extracted: newValue,
          method: 'manual_edit',
        }],
      };
    }
  }

  return {
    ...clause,
    bank_of_changes,
    edit_history: {
      ...currentHistory,
      bank_of_changes: appendedHistory,
    },
  };
}

export const CrossCheckClauseCard: React.FC<CrossCheckClauseCardProps> = ({
  clause,
  index,
  projectId: projectIdProp,
  subProjectId,
  onToggleLock,
  isUpdatingLock = false,
  onResponseSubmitted,
  onResponseUndone,
  submitRevertContextForClause,
  pastAgreementPlaybookXmlById = {},
  pastAgreementPlaybookSideById = {},
  pastAgreementPlaybookFileNameById = {},
  pastAgreementPlaybookFileIdById = {},
  pastAgreementPlaybookFinalVersionById = {},
  pastAgreementPlaybookPlainTextById = {},
  pastAgreementPlaybookAuthorById = {},
  pastAgreementAuthorFilter = null,
  pastAgreementPlaybookTimestampById = {},
  pastAgreementTimeFilter = null,
  pastAgreementPlaybooksLoading = false,
  pastAgreementDomain = 'construction-loan',
  onGoToSalesAgreementSource,
  isSalesAgreementSourceActive = false,
  isSalesAgreementSourceLoading = false,
  onMoveClause,
  onMoveClauseMatter,
  onAnalyzeAgain,
  onAskAgent,
  isMutating = false,
  kebabVariant = 'mcc',
  crossCheckSolution: crossCheckSolutionProp,
  externalAiStreaming = false,
  externalEditedXml = null,
  cardHeaderRef,
  onClauseEdited,
  onActionBanner,
  onBrainShareUpdated,
}) => {
  const crossCheckSolution =
    crossCheckSolutionProp ?? crossCheckSolutionFromKebabVariant(kebabVariant);
  const featureCtx = React.useMemo<CrossCheckFeatureContext>(
    () => ({ solution: crossCheckSolution }),
    [crossCheckSolution],
  );
  const [salesExpanded, setSalesExpanded] = React.useState(false);
  const [bocExpanded, setBocExpanded] = React.useState(false);
  const [showInsightModal, setShowInsightModal] = React.useState(false);
  const [showViewInAgreementConfirm, setShowViewInAgreementConfirm] = React.useState(false);
  const [showExplanationModal, setShowExplanationModal] = React.useState(false);
  const [insightAiSuggestions, setInsightAiSuggestions] = React.useState<InsightAiSuggestion[] | null>(null);
  const [isLoadingInsightAi, setIsLoadingInsightAi] = React.useState(false);
  const insightAiGenerationTriggeredRef = React.useRef(false);
  const goToInFlightRef = React.useRef(false);
  const [editingCr, setEditingCr] = React.useState(false);
  const [crEditMode, setCrEditMode] = React.useState<'plain' | 'track-changes'>('plain');
  const [crEditValue, setCrEditValue] = React.useState('');
  const crEditValueRef = React.useRef('');
  const updateCrEditValue = React.useCallback((value: string) => {
    crEditValueRef.current = value;
    setCrEditValue(value);
  }, []);
  const crEditContentRef = React.useRef<HTMLDivElement | null>(null);
  /** OOXML + accepted text at Edit click — incremental save diffs only the user's delta. */
  const crEditBaselineXmlRef = React.useRef<string | null>(null);
  const crEditBaselineAcceptedRef = React.useRef<string>('');
  const crEditLastAcceptedRef = React.useRef('');
  const crEditRefreshingRef = React.useRef(false);
  const crEditComposingRef = React.useRef(false);
  const crEditSessionInitRef = React.useRef(false);
  const crEditPendingKeyRef = React.useRef<string | null>(null);
  /** Selection at keydown — browser collapses it before the `input` event fires. */
  const crEditPendingSelectionRef = React.useRef<{ start: number; end: number } | null>(null);
  /** Accepted text at keydown — pairs with pendingSelection (OOXML coords, not crEditValueRef). */
  const crEditPendingAcceptedRef = React.useRef<string | null>(null);
  /** Caret restored after last synchronous preview refresh (keyboard typing source of truth). */
  const crEditExpectedCaretRef = React.useRef(0);
  /** Last live-preview OOXML — incremental diff base for the next keystroke (Word-like). */
  const crEditPreviewXmlRef = React.useRef<string | null>(null);
  /** Set when beforeinput handled paste/cut so input does not double-apply. */
  const crEditBeforeInputHandledRef = React.useRef(false);
  const crEditUndoStackRef = React.useRef<CrEditHistorySnapshot[]>([]);
  const crEditRedoStackRef = React.useRef<CrEditHistorySnapshot[]>([]);
  /** Skip pushing undo while restoring from undo/redo. */
  const crEditRestoringRef = React.useRef(false);

  const resetCrEditHistory = React.useCallback(() => {
    crEditUndoStackRef.current = [];
    crEditRedoStackRef.current = [];
  }, []);

  const pushCrEditUndo = React.useCallback((snapshot: CrEditHistorySnapshot) => {
    const stack = crEditUndoStackRef.current;
    stack.push(snapshot);
    if (stack.length > CR_EDIT_UNDO_LIMIT) {
      stack.shift();
    }
    crEditRedoStackRef.current = [];
  }, []);
  const [editingBoc, setEditingBoc] = React.useState(false);
  const [bocEditValue, setBocEditValue] = React.useState('');
  const bocEditTextareaRef = React.useRef<HTMLTextAreaElement>(null);
  const adjustBocEditTextareaHeight = React.useCallback(() => {
    const el = bocEditTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
    el.style.overflowY = 'hidden';
  }, []);
  React.useLayoutEffect(() => {
    if (!editingBoc) return;
    adjustBocEditTextareaHeight();
  }, [editingBoc, bocEditValue, adjustBocEditTextareaHeight]);
  const [addingBoc, setAddingBoc] = React.useState(false);
  const [bocNewValue, setBocNewValue] = React.useState('');
  const [savingBoc, setSavingBoc] = React.useState(false);
  const [showBrainShareModal, setShowBrainShareModal] = React.useState(false);
  const [sharingBrainShare, setSharingBrainShare] = React.useState(false);
  const [pendingBrainShare, setPendingBrainShare] = React.useState<{
    newValue: string;
    isNewOption: boolean;
  } | null>(null);
  const [selectedBocIndex, setSelectedBocIndex] = React.useState(0);
  // Multi-select BoC: ordered list of checked option indices (preserves selection order)
  const [bocCheckedIndices, setBocCheckedIndices] = React.useState<number[]>([]);
  const [bocCombinedText, setBocCombinedText] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [submitResult, setSubmitResult] = React.useState<'success' | 'error' | null>(null);
  /** After AI suggestion: edited XML with track changes (like Negotiator currentEditedXml) */
  const [currentEditedXml, setCurrentEditedXml] = React.useState<string | null>(null);
  /** Client request OOXML after Accept (Word + card preview aligned with accepted text). */
  const [localAcceptedCrXml, setLocalAcceptedCrXml] = React.useState<string | null>(null);
  const [isStreaming, setIsStreaming] = React.useState(false);
  const [aiSourceMenuOpen, setAiSourceMenuOpen] = React.useState(false);
  const aiMenuRef = React.useRef<HTMLDivElement>(null);
  /** Bumped when the user clears or switches clause — ignores late AI stream results. */
  const aiSuggestionGenerationRef = React.useRef(0);

  // Past negotiations (Construction Loan) – simple read-only display of up to 5 matches
  const pastAgreementOptions = React.useMemo(() => {
    const pa: any = (clause as any).past_agreement;
    if (!pa || !Array.isArray(pa?.options)) return null;
    const options = pa.options as PastAgreementOption[];
    const filtered = filterPastAgreementOptions(options, {
      authorFilter: pastAgreementAuthorFilter,
      timeFilter: pastAgreementTimeFilter,
      authorById: pastAgreementPlaybookAuthorById,
      timestampById: pastAgreementPlaybookTimestampById,
    });
    return filtered.length > 0 ? filtered : null;
  }, [
    clause,
    pastAgreementAuthorFilter,
    pastAgreementTimeFilter,
    pastAgreementPlaybookAuthorById,
    pastAgreementPlaybookTimestampById,
  ]);

  React.useEffect(() => {
    setSelectedPastAgreementIndex(0);
    setPastAgreementExpanded(false);
  }, [pastAgreementAuthorFilter, pastAgreementTimeFilter, clause.id]);
  const [pastAgreementExpanded, setPastAgreementExpanded] = React.useState(false);
  const [selectedPastAgreementIndex, setSelectedPastAgreementIndex] = React.useState(0);
  const pastAgreementXmlById = pastAgreementPlaybookXmlById;
  const pastAgreementSideById = pastAgreementPlaybookSideById;
  const pastAgreementFileNameById = pastAgreementPlaybookFileNameById;
  const pastAgreementFileIdById = pastAgreementPlaybookFileIdById;
  const pastAgreementFinalVersionById = pastAgreementPlaybookFinalVersionById;
  const pastAgreementPlainTextById = pastAgreementPlaybookPlainTextById;
  const isPastAgreementXmlLoading = pastAgreementPlaybooksLoading;

  const isPastAgreementOptionFinalVersion = React.useCallback(
    (opt: { id?: string; is_final_version?: boolean }) =>
      opt.is_final_version === true ||
      (opt.id ? pastAgreementFinalVersionById[opt.id] === true : false),
    [pastAgreementFinalVersionById],
  );

  /** Resolve paragraph plain text for a playbook id — batch map first, then legacy opt.text. */
  const getPastAgreementPlainText = React.useCallback(
    (optionId: string, legacyText?: string) =>
      pastAgreementPlainTextById[optionId] || legacyText || '',
    [pastAgreementPlainTextById],
  );

  const getPastAgreementSourceDisplay = React.useCallback(
    (playbookId: string) => {
      if (
        isPastAgreementXmlLoading &&
        !pastAgreementFileNameById[playbookId] &&
        !pastAgreementFileIdById[playbookId]
      ) {
        return 'Loading…';
      }
      const name = pastAgreementFileNameById[playbookId];
      if (name?.trim()) return name.trim();
      const fid = pastAgreementFileIdById[playbookId];
      if (fid?.trim()) return fid.trim();
      return 'Source unavailable';
    },
    [isPastAgreementXmlLoading, pastAgreementFileNameById, pastAgreementFileIdById],
  );
  const [pastAgreementExplanationModal, setPastAgreementExplanationModal] = React.useState<{
    text: string;
    rank: number;
  } | null>(null);
  const [pastAgreementSourceModal, setPastAgreementSourceModal] = React.useState<{
    rank: number;
    playbookId: string;
    searchText?: string;
  } | null>(null);
  const badge = getStatusBadge(clause);
  const clauseNumberDisplay = formatClauseNumberForDisplay(clause.clause_number);
  const bocText = getPrimaryBankOfChangesText(clause.bank_of_changes);
  const bocCount = getBocOptionCount(clause.bank_of_changes);
  const bocOptions = getBocOptions(clause);

  // Clamp selected BoC index when options change (e.g. different clause or list length)
  const safeSelectedBocIndex = React.useMemo(() => {
    if (!bocOptions || bocOptions.length === 0) return 0;
    return Math.min(Math.max(0, selectedBocIndex), bocOptions.length - 1);
  }, [bocOptions, selectedBocIndex]);

  // Displayed BoC text: selected option when list exists, otherwise primary
  const displayBocText = React.useMemo(() => {
    if (bocOptions && bocOptions.length > 0 && bocOptions[safeSelectedBocIndex]) {
      return bocOptions[safeSelectedBocIndex].text;
    }
    return bocText;
  }, [bocOptions, safeSelectedBocIndex, bocText]);

  const safeSelectedPastAgreementIndex = React.useMemo(() => {
    if (!pastAgreementOptions || pastAgreementOptions.length === 0) return 0;
    return Math.min(Math.max(0, selectedPastAgreementIndex), pastAgreementOptions.length - 1);
  }, [pastAgreementOptions, selectedPastAgreementIndex]);

  // Reset selected indices and AI suggestion when switching clause
  React.useEffect(() => {
    setSelectedBocIndex(0);
    setSelectedPastAgreementIndex(0);
    setBocCheckedIndices([]);
    setBocCombinedText('');
    aiSuggestionGenerationRef.current += 1;
    setCurrentEditedXml(null);
    setLocalAcceptedCrXml(null);
    crEditBaselineXmlRef.current = null;
    crEditBaselineAcceptedRef.current = '';
    setIsStreaming(false);
    setAiSourceMenuOpen(false);
    setInsightAiSuggestions(null);
    setIsLoadingInsightAi(false);
    insightAiGenerationTriggeredRef.current = false;
  }, [clause.id]);

  React.useEffect(() => {
    if (externalEditedXml) {
      setCurrentEditedXml(externalEditedXml);
    }
  }, [externalEditedXml]);

  const isStreamingEffective = isStreaming || externalAiStreaming;

  const hasAiSuggestion = Boolean(currentEditedXml);
  const isResponseSubmitted = Boolean(clause.submission_status?.response_submitted);

  const handleClearAiSuggestion = React.useCallback(() => {
    aiSuggestionGenerationRef.current += 1;
    setCurrentEditedXml(null);
    setLocalAcceptedCrXml(null);
    setIsStreaming(false);
    setAiSourceMenuOpen(false);
  }, []);

  const selectedPastAgreementOption =
    pastAgreementOptions && pastAgreementOptions.length > 0
      ? pastAgreementOptions[safeSelectedPastAgreementIndex]
      : null;

  const displayPastAgreementText = React.useMemo(() => {
    if (!selectedPastAgreementOption) return '';
    return selectedPastAgreementOption.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
      : (selectedPastAgreementOption.text ?? '');
  }, [selectedPastAgreementOption, getPastAgreementPlainText]);

  const selectedPastAgreementXml = React.useMemo(() => {
    if (!selectedPastAgreementOption?.id) return null;
    return pastAgreementXmlById[selectedPastAgreementOption.id] || null;
  }, [selectedPastAgreementOption, pastAgreementXmlById]);

  const selectedPastAgreementHtml = React.useMemo(() => {
    if (!selectedPastAgreementXml) return null;
    try { return TrackChangesParser.convertOoxmlToHtml(selectedPastAgreementXml); } catch { return null; }
  }, [selectedPastAgreementXml]);
  const selectedPastAgreementSide = React.useMemo<'our_side' | 'their_side' | null>(() => {
    if (!selectedPastAgreementOption?.id) return null;
    return pastAgreementSideById[selectedPastAgreementOption.id] || null;
  }, [selectedPastAgreementOption, pastAgreementSideById]);

  const selectedPastAgreementDir = React.useMemo((): 'rtl' | 'ltr' => {
    if (selectedPastAgreementXml) {
      try {
        return getDir(TrackChangesParser.extractText(selectedPastAgreementXml)) as 'rtl' | 'ltr';
      } catch {
        // Fall back to text below
      }
    }
    const plainText = selectedPastAgreementOption?.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
      : (selectedPastAgreementOption?.text ?? '');
    return getDir(plainText) as 'rtl' | 'ltr';
  }, [selectedPastAgreementXml, selectedPastAgreementOption, getPastAgreementPlainText]);

  const hasInsights = clause.insights && clause.insights.length > 0;
  const primaryInsight = hasInsights ? clause.insights![0] : null;
  const isLocked = clause.lock_status?.locked;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [isMoveSubmenuOpen, setIsMoveSubmenuOpen] = React.useState(false);
  const [moveSubmenuPosition, setMoveSubmenuPosition] = React.useState<{ top: number; left: number } | null>(null);
  const menuRef = React.useRef<HTMLDivElement | null>(null);
  const moveMenuItemRef = React.useRef<HTMLButtonElement | null>(null);
  const moveSubmenuRef = React.useRef<HTMLDivElement | null>(null);
  const MOVE_SUBMENU_WIDTH = 176;

  const isClauseSubmitted =
    !!clause.submission_status?.schedule_submitted ||
    !!clause.submission_status?.response_submitted;
  const moveBlockedMessage =
    'This clause has already been submitted. Undo the submission before moving it.';
  const isClKebab = kebabVariant === 'construction-loan';
  const showKebabMenu =
    onMoveClause ||
    onMoveClauseMatter ||
    onAskAgent ||
    (onAnalyzeAgain &&
      (isClKebab ||
        clause.documentType === 'sales_agreement' ||
        clause.documentType === 'appendix_a'));

  const updateMoveSubmenuPosition = React.useCallback(() => {
    const anchor = moveMenuItemRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 4;
    let left = rect.left - MOVE_SUBMENU_WIDTH - gap;
    if (left < 8) {
      left = rect.right + gap;
    }
    setMoveSubmenuPosition({ top: rect.top, left });
  }, []);

  React.useEffect(() => {
    if (!isMoveSubmenuOpen) {
      setMoveSubmenuPosition(null);
      return;
    }
    updateMoveSubmenuPosition();
    window.addEventListener('resize', updateMoveSubmenuPosition);
    window.addEventListener('scroll', updateMoveSubmenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMoveSubmenuPosition);
      window.removeEventListener('scroll', updateMoveSubmenuPosition, true);
    };
  }, [isMoveSubmenuOpen, updateMoveSubmenuPosition]);

  React.useEffect(() => {
    if (!isMenuOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const insideMenu = menuRef.current?.contains(target);
      const insideMoveSubmenu = moveSubmenuRef.current?.contains(target);
      if (!insideMenu && !insideMoveSubmenu) {
        setIsMenuOpen(false);
        setIsMoveSubmenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  React.useEffect(() => {
    setIsMenuOpen(false);
    setIsMoveSubmenuOpen(false);
  }, [clause.id]);

  const clientRequestXml = React.useMemo(() => getClientRequestXml(clause), [clause]);
  const clientRequestPlainText = React.useMemo(() => getClientRequestPlainText(clause, clientRequestXml), [clause, clientRequestXml]);

  const displayXml = currentEditedXml ?? localAcceptedCrXml ?? clientRequestXml;
  const clientRequestDisplayHtml = React.useMemo(() => {
    const hasUserEditedXml = Boolean(localAcceptedCrXml || currentEditedXml);
    const parts = clause.track_changes?.track_changes_parts;
    if (!hasUserEditedXml && Array.isArray(parts) && parts.length > 0) {
      const displayParts = getDisplayTrackChangesParts(clause, featureCtx);
      const htmlParts = displayParts
        .filter((part) => typeof part.paragraph_xml === 'string' && part.paragraph_xml.trim())
        .map((part) => convertClientRequestXmlToHtml(part.paragraph_xml as string));
      if (htmlParts.length > 0) {
        return htmlParts.join('');
      }
    }
    const xml = displayXml ?? clientRequestXml;
    return xml ? convertClientRequestXmlToHtml(xml) : null;
  }, [clause.track_changes?.track_changes_parts, displayXml, clientRequestXml, localAcceptedCrXml, currentEditedXml, clause.id]);

  // Hybrid preview: the granular redline the hybrid engine will apply, diffed against the LIVE
  // paragraph (existing revisions preserved). Computed off a debounced, read-only Word.run. Falls
  // back to the legacy AI-OOXML redline when it can't compute (e.g. no linked paragraph) so a redline
  // ALWAYS renders — never plain text.
  const [hybridEnabled, setHybridEnabled] = React.useState(isHybridApplyEnabled);
  const [hybridPreviewHtml, setHybridPreviewHtml] = React.useState<string | null>(null);
  React.useEffect(() => {
    setHybridEnabled(isHybridApplyEnabled());
    return subscribeHybridApplyEnabled(() => setHybridEnabled(isHybridApplyEnabled()));
  }, []);

  const hybridPreviewTarget = React.useMemo(() => {
    const submitXml = currentEditedXml ?? localAcceptedCrXml;
    const accepted = submitXml
      ? TrackChangesParser.extractAcceptedText(submitXml)
      : clientRequestPlainText;
    return (accepted || '').replace(/\s+/g, ' ').trim();
  }, [currentEditedXml, localAcceptedCrXml, clientRequestPlainText]);

  const paragraphIndexForPreview =
    typeof clause.paragraph_index === 'number' && !Number.isNaN(clause.paragraph_index)
      ? clause.paragraph_index
      : null;
  const hasSuggestionToPreview = Boolean(currentEditedXml || localAcceptedCrXml);

  React.useEffect(() => {
    if (!hybridEnabled || paragraphIndexForPreview == null || !hybridPreviewTarget || !hasSuggestionToPreview) {
      setHybridPreviewHtml(null);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      void previewHybridOps(paragraphIndexForPreview, hybridPreviewTarget).then((preview) => {
        if (!cancelled) setHybridPreviewHtml(preview?.html ?? null);
      });
    }, 350);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [hybridEnabled, paragraphIndexForPreview, hybridPreviewTarget, hasSuggestionToPreview]);

  /** Redline shown in the card: the hybrid preview when available, else the legacy AI-OOXML redline. */
  const effectivePreviewHtml =
    hybridEnabled && hybridPreviewHtml != null ? hybridPreviewHtml : clientRequestDisplayHtml;

  /** Plain text currently shown in Client Request (AI suggestion, accepted TC, or original). */
  const displayedClientRequestPlainText = React.useMemo(() => {
    if (displayXml) {
      try {
        return TrackChangesParser.extractAcceptedText(displayXml).replace(/\s+/g, ' ').trim();
      } catch {
        /* fall through */
      }
    }
    return clientRequestPlainText;
  }, [displayXml, clientRequestPlainText]);
  const canEditWithTrackChangesPreview = React.useMemo(() => {
    if (!displayXml) return false;
    if (hasAiSuggestion || localAcceptedCrXml) return true;
    try {
      return TrackChangesParser.hasTrackChangesMarkup(displayXml);
    } catch {
      return false;
    }
  }, [displayXml, hasAiSuggestion, localAcceptedCrXml]);
  const hasTrackChangesInClientRequest = React.useMemo(
    () => Boolean(displayXml && TrackChangesParser.hasTrackChangesMarkup(displayXml)),
    [displayXml],
  );
  const canAcceptTrackChanges =
    !isResponseSubmitted &&
    hasTrackChangesInClientRequest &&
    clause.paragraph_index != null &&
    !Number.isNaN(Number(clause.paragraph_index));

  /** Direction for Client Request: use plain text for detection, not raw XML (XML would be detected as LTR). */
  const clientRequestDir = React.useMemo((): 'rtl' | 'ltr' => {
    if (editingCr && crEditValue) return getDir(crEditValue) as 'rtl' | 'ltr';
    if (isStreamingEffective && clientRequestXml) {
      try {
        const plain = TrackChangesParser.extractText(clientRequestXml);
        return getDir(plain) as 'rtl' | 'ltr';
      } catch {
        return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
      }
    }
    if (displayXml) {
      try {
        const plain = TrackChangesParser.extractText(displayXml);
        return getDir(plain) as 'rtl' | 'ltr';
      } catch {
        return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
      }
    }
    return getDir(clientRequestPlainText) as 'rtl' | 'ltr';
  }, [editingCr, crEditValue, isStreamingEffective, clientRequestXml, displayXml, clientRequestPlainText]);

  /** Accept track changes in Word and mark clause as response-submitted (same outcome as Submit to Response). */
  const handleAcceptClientRequestTrackChanges = React.useCallback(async () => {
    if (isSubmitting || isResponseSubmitted) return;
    const paragraphIndex = clause.paragraph_index;
    if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex))) return;
    const sourceXml = displayXml;
    if (!sourceXml || !TrackChangesParser.hasTrackChangesMarkup(sourceXml)) return;

    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      await DocumentReader.navigateToParagraph(Number(paragraphIndex));
      const { acceptedCount, paragraphOoxml, revertContext } =
        await RedlineInserter.acceptTrackedChangesInParagraph(Number(paragraphIndex));

      if (acceptedCount === 0) {
        console.warn(
          '[CrossCheckClauseCard] Accept: no Word tracked changes found in paragraph',
        );
        setSubmitResult('error');
        setTimeout(() => setSubmitResult(null), 2500);
        return;
      }

      if (paragraphOoxml) {
        setLocalAcceptedCrXml(paragraphOoxml);
        if (currentEditedXml) {
          setCurrentEditedXml(paragraphOoxml);
        }
      }

      if (subProjectId) {
        try {
          await apiService.updateClauseResponseSubmissionStatus(
            subProjectId,
            clause.id,
            true,
            revertContext,
          );
        } catch (backendErr) {
          console.warn(
            '[CrossCheckClauseCard] Accept: backend submit status update failed (Word changes still applied):',
            backendErr,
          );
        }
      }
      onResponseSubmitted?.(clause.id, revertContext);
      setSubmitResult('success');
      setTimeout(() => setSubmitResult(null), 2500);
    } catch (err) {
      console.error('[CrossCheckClauseCard] Accept track changes failed:', err);
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    isResponseSubmitted,
    clause.id,
    clause.paragraph_index,
    currentEditedXml,
    displayXml,
    subProjectId,
    onResponseSubmitted,
  ]);

  /** Direction for Sales Agreement text. */
  const salesAgreementDir = React.useMemo(
    () => getDir(clause.sales_agreement) as 'rtl' | 'ltr',
    [clause.sales_agreement]
  );

  const resolvedProjectId = projectIdProp ?? undefined;

  // ─── AI source availability ──────────────────────────────────────────────────
  const hasBocForAi = Boolean(displayBocText?.trim());
  const hasPaForAi = Boolean(
    selectedPastAgreementOption?.id
      ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text).trim()
      : selectedPastAgreementOption?.text?.trim()
  );

  // ─── Click-outside handler for AI source menu ────────────────────────────────
  React.useEffect(() => {
    if (!aiSourceMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target as Node)) {
        setAiSourceMenuOpen(false);
      }
    };
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAiSourceMenuOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', keyHandler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('keydown', keyHandler);
    };
  }, [aiSourceMenuOpen]);

  // ─── AI (master cross check): bank-of-change / past-agreement suggestion stream ─────
  const handleAiSuggestionFrom = React.useCallback(
    async (
      source: 'bank_of_changes' | 'past_agreement',
      opts?: {
        bankOfChangesOption?: BankOfChangesOption;
        pastAgreementOption?: PastAgreementOption;
      },
    ) => {
      if (!resolvedProjectId || isStreamingEffective) return;

      const paOpt =
        source === 'past_agreement'
          ? opts?.pastAgreementOption ?? selectedPastAgreementOption
          : null;
      const bocOpt =
        source === 'bank_of_changes'
          ? opts?.bankOfChangesOption ?? bocOptions?.[safeSelectedBocIndex]
          : null;

      const referenceText =
        source === 'past_agreement'
          ? (paOpt?.id
              ? getPastAgreementPlainText(paOpt.id, paOpt.text)
              : (paOpt?.text ?? '')
            ).trim()
          : (bocOpt?.text ?? displayBocText).trim();

      const referenceExplanation =
        source === 'past_agreement'
          ? (paOpt?.explanation ?? '').trim() || undefined
          : (bocOpt?.internal_note ?? '').trim() || undefined;

      if (!referenceText) return;

    const crXml = clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
    const crText =
      clientRequestPlainText ||
      TrackChangesParser.extractAcceptedText(crXml) ||
      TrackChangesParser.extractText(crXml);

    const generation = aiSuggestionGenerationRef.current + 1;
    aiSuggestionGenerationRef.current = generation;
    setIsStreaming(true);
    let accumulatedText = '';
    let finalSuggestedText = '';

    try {
      for await (const chunk of apiService.generateBankOfChangeSuggestionStream(
        resolvedProjectId,
        crText,
        crXml,
        referenceText,
        source,
        referenceExplanation
      )) {
        if (chunk.type === 'text' && chunk.content) {
          accumulatedText += chunk.content;
        } else if (chunk.type === 'complete') {
          finalSuggestedText = chunk.suggested_text ?? accumulatedText;
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
        // 'xml_ready' from server is intentionally ignored: we always generate XML
        // client-side via RobustXmlTrackChangesGenerator so that existing track
        // changes in the client request are preserved (same approach as ClauseCard).
      }

      const normCr = crText.replace(/\s+/g, ' ').trim();
      const normFinal = finalSuggestedText.replace(/\s+/g, ' ').trim();
      const engineNoOp = Boolean(normFinal) && normFinal === normCr;

      // Always generate XML client-side so that pre-existing w:ins/w:del elements
      // in the original client-request XML are threaded through correctly.
      if (generation !== aiSuggestionGenerationRef.current) return;
      if (engineNoOp) return;
      if (finalSuggestedText && crXml) {
        try {
          const { RobustXmlTrackChangesGenerator } = await import('../../features/rephrasing/RobustXmlTrackChangesGenerator');
          const suggestedXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            crXml,
            finalSuggestedText,
            'AI Suggestion'
          );
          if (generation !== aiSuggestionGenerationRef.current) return;
          setCurrentEditedXml(suggestedXml);
        } catch (e) {
          console.error('[CrossCheckClauseCard] Generate track-changes XML failed:', e);
          if (generation !== aiSuggestionGenerationRef.current) return;
          setCurrentEditedXml(crXml);
        }
      }
    } catch (err) {
      reportClauseAiError(err, onActionBanner, 'AI suggestion failed');
    } finally {
      if (generation === aiSuggestionGenerationRef.current) {
        setIsStreaming(false);
      }
    }
    },
    [
      resolvedProjectId,
      isStreaming,
      displayBocText,
      selectedPastAgreementOption,
      getPastAgreementPlainText,
      bocOptions,
      safeSelectedBocIndex,
      clientRequestXml,
      clientRequestPlainText,
      onActionBanner,
    ],
  );

  const applyInsightSuggestionToClientRequest = React.useCallback(
    (suggestedText: string, generation?: number) => {
      const previewXml = buildAiSuggestionPreviewXml(
        clientRequestXml,
        clientRequestPlainText,
        suggestedText,
      );
      if (!previewXml) {
        return;
      }
      if (generation !== undefined && generation !== aiSuggestionGenerationRef.current) return;
      setCurrentEditedXml(previewXml);
    },
    [clientRequestXml, clientRequestPlainText, clause.id],
  );

  const handleGenerateInsightAiSuggestions = React.useCallback(async () => {
    if (!subProjectId || !hasInsights || !clientRequestPlainText?.trim()) {
      insightAiGenerationTriggeredRef.current = false;
      return;
    }
    if (isLoadingInsightAi) return;

    const insights = clause.insights!;
    const insightsForApi = formatInsightsForApi(insights, clause.module_type);

    try {
      setIsLoadingInsightAi(true);
      setInsightAiSuggestions(
        insights.map((insight, idx) => ({
          clauseReference: `Insight ${idx + 1}`,
          explanation: insight.explanation || '',
          suggestedResponse: '',
        })),
      );

      let accumulatedText = '';
      let finalSuggestions: InsightAiSuggestion[] | null = null;

      for await (const chunk of apiService.generateInsightResponsesStream(
        subProjectId,
        clientRequestPlainText,
        insightsForApi,
      )) {
        if (chunk.type === 'text') {
          accumulatedText += chunk.content || '';
          const extractedResponses = extractAllSuggestedResponses(accumulatedText);
          setInsightAiSuggestions((prev) => {
            if (!prev) return prev;
            return prev.map((suggestion, idx) => ({
              ...suggestion,
              suggestedResponse: extractedResponses[idx] ?? suggestion.suggestedResponse,
            }));
          });
        } else if (chunk.type === 'complete') {
          finalSuggestions =
            chunk.suggestions?.map((s, idx) => ({
              clauseReference: `Insight ${idx + 1}`,
              explanation: insights[idx]?.explanation || '',
              suggestedResponse: s.suggestedResponse ?? null,
            })) ?? null;
          if (finalSuggestions) {
            setInsightAiSuggestions(finalSuggestions);
          }
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
      }

      if (!finalSuggestions) {
        const extracted = extractAllSuggestedResponses(accumulatedText);
        if (extracted.length > 0) {
          setInsightAiSuggestions(
            insights.map((insight, idx) => ({
              clauseReference: `Insight ${idx + 1}`,
              explanation: insight.explanation || '',
              suggestedResponse: extracted[idx] ?? null,
            })),
          );
        }
      }
    } catch (err) {
      reportClauseAiError(err, onActionBanner, 'Insight AI suggestions failed');
      insightAiGenerationTriggeredRef.current = false;
    } finally {
      setIsLoadingInsightAi(false);
    }
  }, [
    subProjectId,
    hasInsights,
    clientRequestPlainText,
    clause.insights,
    clause.module_type,
    isLoadingInsightAi,
    onActionBanner,
  ]);

  const handleApplyInsightSuggestion = React.useCallback(
    (suggestedText: string | null | undefined) => {
      if (!suggestedText?.trim()) return;
      aiSuggestionGenerationRef.current += 1;
      applyInsightSuggestionToClientRequest(suggestedText);
      setShowInsightModal(false);
      setInsightAiSuggestions(null);
      setIsLoadingInsightAi(false);
      insightAiGenerationTriggeredRef.current = false;
    },
    [applyInsightSuggestionToClientRequest],
  );

  const closeInsightModal = React.useCallback(() => {
    setShowInsightModal(false);
    setInsightAiSuggestions(null);
    setIsLoadingInsightAi(false);
    insightAiGenerationTriggeredRef.current = false;
  }, []);

  const runAlreadyExistAiFlow = React.useCallback(async () => {
    if (!hasInsights || !subProjectId || isStreamingEffective) return;

    const insights = clause.insights!;
    if (insights.length > 1) {
      setShowInsightModal(true);
      return;
    }

    const crText = clientRequestPlainText.trim();
    if (!crText) return;

    const insightsForApi = formatInsightsForApi(insights, clause.module_type);
    const generation = aiSuggestionGenerationRef.current + 1;
    aiSuggestionGenerationRef.current = generation;
    setIsStreaming(true);

    let accumulatedText = '';
    let finalSuggestedText = '';

    try {
      for await (const chunk of apiService.generateInsightResponsesStream(
        subProjectId,
        crText,
        insightsForApi,
      )) {
        if (generation !== aiSuggestionGenerationRef.current) return;
        if (chunk.type === 'text') {
          accumulatedText += chunk.content || '';
        } else if (chunk.type === 'complete') {
          const suggestions = chunk.suggestions || [];
          if (suggestions.length > 0) {
            finalSuggestedText =
              suggestions[0].suggestedResponse || extractSuggestedResponse(accumulatedText);
          }
        } else if (chunk.type === 'error') {
          throw new Error(chunk.error);
        }
      }

      if (!finalSuggestedText) {
        finalSuggestedText = extractSuggestedResponse(accumulatedText);
      }
      if (finalSuggestedText) {
        applyInsightSuggestionToClientRequest(finalSuggestedText, generation);
      }
    } catch (err) {
      reportClauseAiError(err, onActionBanner, 'AI - Already Exist failed');
    } finally {
      if (generation === aiSuggestionGenerationRef.current) {
        setIsStreaming(false);
      }
    }
  }, [
    hasInsights,
    subProjectId,
    isStreamingEffective,
    clause.insights,
    clause.module_type,
    clientRequestPlainText,
    applyInsightSuggestionToClientRequest,
    onActionBanner,
  ]);

  const handleAlreadyExistAI = React.useCallback(() => {
    if (!hasInsights || !subProjectId || isStreamingEffective) return;

    const insights = clause.insights ?? [];
    const insightForGoTo = pickInsightForSalesAgreementGoTo(insights);
    const canOfferSalesAgreementGoTo =
      Boolean(onGoToSalesAgreementSource) &&
      insightForGoTo != null &&
      getInsightExistingClauseSearchText(insightForGoTo).length > 0;

    if (canOfferSalesAgreementGoTo) {
      setShowViewInAgreementConfirm(true);
      return;
    }

    void runAlreadyExistAiFlow();
  }, [
    hasInsights,
    subProjectId,
    isStreamingEffective,
    clause.insights,
    onGoToSalesAgreementSource,
    runAlreadyExistAiFlow,
  ]);

  React.useEffect(() => {
    if (!showInsightModal) {
      insightAiGenerationTriggeredRef.current = false;
      return;
    }
    if (
      showInsightModal &&
      clientRequestPlainText?.trim() &&
      hasInsights &&
      !insightAiSuggestions &&
      !isLoadingInsightAi &&
      !insightAiGenerationTriggeredRef.current
    ) {
      insightAiGenerationTriggeredRef.current = true;
      void handleGenerateInsightAiSuggestions();
    }
  }, [
    showInsightModal,
    clientRequestPlainText,
    hasInsights,
    insightAiSuggestions,
    isLoadingInsightAi,
    handleGenerateInsightAiSuggestions,
  ]);

  // ─── Submit to Response (same submission state as Submit as-is) ───────────────
  const handleSubmitToResponse = React.useCallback(async () => {
    if (isSubmitting || isResponseSubmitted) return;

    const salesAgreementText = (clause.sales_agreement || '').replace(/\s+/g, ' ').trim();
    // AI suggestion (currentEditedXml) or manual card edit (localAcceptedCrXml).
    const submitXml = currentEditedXml ?? localAcceptedCrXml;
    let newText: string;
    let trackChangesHtml: string;
    let searchOriginal: string;

    if (submitXml) {
      // Diff was computed against clientRequestPlainText (Word paragraph baseline).
      newText = TrackChangesParser.extractAcceptedText(submitXml).replace(/\s+/g, ' ').trim();
      trackChangesHtml = TrackChangesParser.convertOoxmlToHtml(submitXml);
      searchOriginal = clientRequestPlainText.replace(/\s+/g, ' ').trim() || salesAgreementText || newText;
    } else {
      // No card edits: apply the raw client request on top of the original sales agreement.
      newText = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      trackChangesHtml = clientRequestXml ? TrackChangesParser.convertOoxmlToHtml(clientRequestXml) : '';
      searchOriginal = salesAgreementText || newText;
    }
    if (!searchOriginal) {
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
      return;
    }

    setIsSubmitting(true);
    setSubmitResult(null);
    try {
      let paragraphIndex = clause.paragraph_index;

      // When the backend couldn't link this clause to a paragraph (e.g.
      // clause.clause_number === "לא נמצא" / "not found"), paragraph_index is null.
      // RedlineInserter.applyParagraphFromEditedOoxml no longer silently falls
      // back to paragraph 0 (which used to corrupt the document title and trigger
      // a Word GeneralException). Resolve a real paragraph here using the
      // accepted sales-agreement text or the user's current cursor position, so
      // both the OOXML and incremental apply paths target the correct paragraph.
      if (paragraphIndex == null || Number.isNaN(Number(paragraphIndex))) {
        const candidates: Array<{ kind: string; text: string }> = [];
        if (salesAgreementText) candidates.push({ kind: 'salesAgreement', text: salesAgreementText });
        const crNorm = clientRequestPlainText.replace(/\s+/g, ' ').trim();
        if (crNorm) candidates.push({ kind: 'clientRequest', text: crNorm });
        for (const candidate of candidates) {
          try {
            const found = await DocumentReader.findParagraphIndexByContent(candidate.text);
            if (found != null && found >= 0) {
              paragraphIndex = found;
              debugLog('CrossCheckClauseCard: submit-to-response resolved paragraph by content', {
                clauseId: clause.id,
                resolvedBy: candidate.kind,
                resolvedParagraphIndex: paragraphIndex,
              });
              break;
            }
          } catch (resolveErr) {
            debugWarn('CrossCheckClauseCard: submit-to-response content resolution failed', {
              clauseId: clause.id,
              kind: candidate.kind,
              error: resolveErr instanceof Error ? resolveErr.message : String(resolveErr),
            });
          }
        }

        if (paragraphIndex == null) {
          try {
            const selectionInfo = await DocumentReader.getSelectionOrParagraph();
            const selectionIdx = selectionInfo?.paragraphIndex;
            const selectionText = (selectionInfo?.text || '').trim();
            // Only accept the selection if the user is actually positioned in a
            // non-empty paragraph (not just at the document start with no cursor).
            if (
              typeof selectionIdx === 'number' &&
              selectionIdx >= 0 &&
              selectionText.length > 0
            ) {
              paragraphIndex = selectionIdx;
              debugLog('CrossCheckClauseCard: submit-to-response resolved paragraph by selection', {
                clauseId: clause.id,
                resolvedParagraphIndex: paragraphIndex,
                selectionTextSnippet: selectionText.slice(0, 80),
              });
            }
          } catch (selErr) {
            debugWarn('CrossCheckClauseCard: submit-to-response selection resolution failed', {
              clauseId: clause.id,
              error: selErr instanceof Error ? selErr.message : String(selErr),
            });
          }
        }

        if (paragraphIndex == null) {
          debugWarn('CrossCheckClauseCard: submit-to-response could not resolve paragraph', {
            clauseId: clause.id,
            clauseNumber: clause.clause_number,
            hasSalesAgreementText: Boolean(salesAgreementText),
            clientRequestSnippet: clientRequestPlainText.slice(0, 80),
          });
          throw new Error(
            'Could not locate the matching paragraph in the document. Place your cursor in the target paragraph and try again.',
          );
        }
      }

      const usesOoxmlIndexForApply = usesOoxmlParagraphIndex(clause, featureCtx);
      const applyResolveOptions = {
        searchText: searchOriginal,
        ooxmlParagraphIndex:
          usesOoxmlIndexForApply && paragraphIndex != null
            ? Number(paragraphIndex)
            : undefined,
        trustBodyParagraphIndex: !usesOoxmlIndexForApply,
      };
      if (usesOoxmlIndexForApply) {
        const resolvedBodyIndex = await DocumentReader.resolveParagraphForApply(
          paragraphIndex ?? undefined,
          applyResolveOptions,
        );
        if (resolvedBodyIndex != null) {
          paragraphIndex = resolvedBodyIndex;
        }
      }

      debugLog('CrossCheckClauseCard: submit-to-response starting', {
        clauseId: clause.id,
        clauseNumber: clause.clause_number,
        paragraphIndex: paragraphIndex ?? null,
        paragraphIndexFromClause: clause.paragraph_index ?? null,
        hasSubmitXml: Boolean(submitXml),
        searchOriginalSnippet: searchOriginal.slice(0, 120),
        newTextSnippet: newText.slice(0, 120),
      });

      // Jump to the target paragraph before applying (same navigation as submit-as-is / undo).
      const navigationAnchor =
        getModuleBNavigationAnchor(clause, featureCtx) || searchOriginal;
      if (usesOoxmlIndexForApply && navigationAnchor) {
        await DocumentReader.navigateToParagraphByContent(navigationAnchor, {
          clauseId: clause.id,
          clauseNumber: clause.clause_number,
          strategy: 'submit_to_response',
          selectFullParagraph: !isTableRowClause(clause, featureCtx),
          ooxmlParagraphIndex:
            paragraphIndex != null ? Number(paragraphIndex) : undefined,
        });
      } else if (paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))) {
        await DocumentReader.navigateToParagraph(Number(paragraphIndex));
      } else if (searchOriginal) {
        await DocumentReader.navigateToParagraphByContent(searchOriginal);
      }

      const useIncremental = trackChangesHtml.length > 0 && (
        trackChangesHtml.includes('track-change-insertion') || trackChangesHtml.includes('track-change-deletion')
      );

      let applyResult: ApplyParagraphResult = { applied: false };
      const useHybridApply = isHybridApplyEnabled();
      if (useHybridApply && paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))) {
        // Behind the Profile "Hybrid apply (beta)" flag. The hybrid engine builds its own
        // visible-state target from the live paragraph, so it only needs the clean accepted target
        // (newText) and the paragraph index. A pre-flight abort is a clean no-op — surfaced as the
        // same "could not apply" error below, never partial corruption.
        const hybrid = await applyViaHybridEngine(Number(paragraphIndex), newText);
        debugLog('CrossCheckClauseCard: submit-to-response hybrid apply', {
          clauseId: clause.id,
          paragraphIndex: paragraphIndex ?? null,
          applied: hybrid.applied,
          reason: hybrid.reason ?? null,
          trace: hybrid.trace,
        });
        // The hybrid engine returns a revertContext (fingerprints + apply window of the changes it
        // added) so Undo can revert exactly those, author-independently.
        applyResult = { applied: hybrid.applied, revertContext: hybrid.revertContext };
      } else if (submitXml) {
        applyResult = await RedlineInserter.applyParagraphFromEditedOoxml(
          submitXml,
          paragraphIndex,
          applyResolveOptions,
        );

        // Live-edit preview OOXML is built incrementally per keystroke and often
        // rejected by Word insertOoxml (GeneralException). Regenerate once with the
        // contiguous simple-edit path (postProcessWordLikeInsSegments) so spacing
        // matches the card before falling back to search-based incremental apply.
        if (!applyResult.applied) {
          const regenBaseline =
            clientRequestXml ||
            TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
          const regenerated = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            regenBaseline,
            newText,
            'User Edit',
            { source: 'submit-to-response', clauseId: clause.id },
            'char',
            { mode: 'simple-edit', simpleEditDiffStrategy: 'contiguous' },
          );
          applyResult = await RedlineInserter.applyParagraphFromEditedOoxml(
            regenerated,
            paragraphIndex,
            applyResolveOptions,
          );
        }
      }

      if (!useHybridApply && !applyResult.applied && useIncremental) {
        applyResult = await RedlineInserter.applyWordLevelChangesIncrementally(
          searchOriginal,
          newText,
          trackChangesHtml,
          paragraphIndex,
        );
      }

      if (!applyResult.applied) {
        debugWarn('CrossCheckClauseCard: submit-to-response apply failed', {
          clauseId: clause.id,
          paragraphIndex: paragraphIndex ?? null,
        });
        throw new Error(
          'Could not apply track changes to the Word paragraph. Try selecting the clause paragraph and submit again.',
        );
      }

      debugLog('CrossCheckClauseCard: submit-to-response succeeded', {
        clauseId: clause.id,
        paragraphIndex: paragraphIndex ?? null,
      });

      // Update card status on backend only (add-in: no file/Drive ops; same endpoint as undo)
      onResponseSubmitted?.(clause.id, applyResult.revertContext);
      if (subProjectId) {
        try {
          await apiService.updateClauseResponseSubmissionStatus(
            subProjectId,
            clause.id,
            true,
            applyResult.revertContext,
          );
        } catch (backendErr) {
          console.warn('[CrossCheckClauseCard] Backend submit status update failed (Word changes still applied):', backendErr);
        }
      }

      setSubmitResult('success');
      setTimeout(() => setSubmitResult(null), 2500);
    } catch (err) {
      console.error('[CrossCheckClauseCard] Submit to response failed:', err);
      setSubmitResult('error');
      setTimeout(() => setSubmitResult(null), 2500);
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, isResponseSubmitted, clause, clientRequestPlainText, clientRequestXml, currentEditedXml, localAcceptedCrXml, subProjectId, onResponseSubmitted]);

  /** Replace the client-request paragraph with source text — same submission/undo as Submit to Response. */
  const applySubmitAsIs = React.useCallback(
    async (sourceText: string, authorLabel: string) => {
      if (isSubmitting || isResponseSubmitted) return;

      const newText = (sourceText || '').replace(/\s+/g, ' ').trim();
      const searchOriginal = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      if (!newText || !searchOriginal) {
        setSubmitResult('error');
        setTimeout(() => setSubmitResult(null), 2500);
        return;
      }

      const crXml =
        clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);

      setIsSubmitting(true);
      setSubmitResult(null);
      try {
        const paragraphIndex = clause.paragraph_index;
        const usesOoxmlIndex = usesOoxmlParagraphIndex(clause, featureCtx);
        const navigationAnchor =
          getModuleBNavigationAnchor(clause, featureCtx) || searchOriginal;
        const resolveOptions = {
          searchText: searchOriginal,
          ooxmlParagraphIndex:
            usesOoxmlIndex && paragraphIndex != null
              ? Number(paragraphIndex)
              : undefined,
          trustBodyParagraphIndex: !usesOoxmlIndex,
        };

        debugLog('CrossCheckClauseCard: submit-as-is starting', {
          clauseId: clause.id,
          clauseNumber: clause.clause_number,
          authorLabel,
          paragraphIndex: paragraphIndex ?? null,
          usesOoxmlIndex,
          navigationAnchorSnippet: navigationAnchor.slice(0, 120),
          searchOriginalSnippet: searchOriginal.slice(0, 120),
          newTextSnippet: newText.slice(0, 120),
        });

        if (usesOoxmlIndex && navigationAnchor) {
          await DocumentReader.navigateToParagraphByContent(navigationAnchor, {
            clauseId: clause.id,
            clauseNumber: clause.clause_number,
            strategy: 'submit_as_is',
            selectFullParagraph: !isTableRowClause(clause, featureCtx),
            ooxmlParagraphIndex:
              paragraphIndex != null ? Number(paragraphIndex) : undefined,
          });
        } else if (paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))) {
          await DocumentReader.navigateToParagraph(Number(paragraphIndex));
        } else if (searchOriginal) {
          await DocumentReader.navigateToParagraphByContent(searchOriginal);
        }

        const applyResult = await RedlineInserter.applySubmitAsIs(
          searchOriginal,
          newText,
          paragraphIndex != null ? Number(paragraphIndex) : undefined,
          authorLabel,
          crXml,
          resolveOptions,
        );
        if (!applyResult.applied) {
          debugWarn('CrossCheckClauseCard: submit-as-is apply failed', {
            clauseId: clause.id,
            paragraphIndex: paragraphIndex ?? null,
            authorLabel,
          });
          throw new Error('Could not apply track changes to the Word paragraph.');
        }

        debugLog('CrossCheckClauseCard: submit-as-is succeeded', {
          clauseId: clause.id,
          paragraphIndex: paragraphIndex ?? null,
          authorLabel,
        });

        onResponseSubmitted?.(clause.id, applyResult.revertContext);
        if (subProjectId) {
          try {
            await apiService.updateClauseResponseSubmissionStatus(
              subProjectId,
              clause.id,
              true,
              applyResult.revertContext,
            );
          } catch (backendErr) {
            console.warn(
              '[CrossCheckClauseCard] Submit as-is: backend status update failed (Word changes still applied):',
              backendErr,
            );
          }
        }
        setSubmitResult('success');
        setTimeout(() => setSubmitResult(null), 2500);
      } catch (err) {
        console.error('[CrossCheckClauseCard] Submit as-is failed:', err);
        setSubmitResult('error');
        setTimeout(() => setSubmitResult(null), 2500);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      isSubmitting,
      isResponseSubmitted,
      clause,
      clientRequestPlainText,
      clientRequestXml,
      subProjectId,
      onResponseSubmitted,
    ],
  );

  const handleSubmitBankOfChangesAsIs = React.useCallback(async () => {
    if (isStreaming || editingBoc) return;
    await applySubmitAsIs(displayBocText, 'Bank of Changes');
  }, [applySubmitAsIs, isStreaming, editingBoc, displayBocText]);

  const handleSubmitPastNegotiationAsIs = React.useCallback(async () => {
    if (isStreaming) return;
    await applySubmitAsIs(displayPastAgreementText, 'Past negotiations');
  }, [applySubmitAsIs, isStreaming, displayPastAgreementText]);

  // ─── Undo Response (reverts Submit to Response or Submit as-is) ─────────────
  const [isUndoing, setIsUndoing] = React.useState(false);
  const [undoNotFoundMessage, setUndoNotFoundMessage] = React.useState<string | null>(null);
  const handleUndoResponse = React.useCallback(async () => {
    if (isUndoing || !isResponseSubmitted) return;

    setIsUndoing(true);
    setUndoNotFoundMessage(null);
    try {
      const beforeText = clause.sales_agreement || '';
      const revertContextToUse =
        submitRevertContextForClause ??
        submitRevertContextFromSubmissionStatus(clause.submission_status);
      const usesOoxmlIndex = usesOoxmlParagraphIndex(clause, featureCtx);
      const effectiveOoxmlIndex = resolveEffectiveParagraphIndex(clause);
      const searchOriginal = clientRequestPlainText.replace(/\s+/g, ' ').trim();
      const applyResolveOptions = {
        searchText: searchOriginal || beforeText.replace(/\s+/g, ' ').trim(),
        ooxmlParagraphIndex:
          usesOoxmlIndex && effectiveOoxmlIndex != null
            ? effectiveOoxmlIndex
            : undefined,
        trustBodyParagraphIndex: !usesOoxmlIndex,
      };

      // Prefer body index captured at submit; re-resolve when OOXML/body indices may diverge.
      let paragraphIndexToUse: number | null =
        revertContextToUse != null && revertContextToUse.paragraphIndex >= 0
          ? revertContextToUse.paragraphIndex
          : effectiveOoxmlIndex;

      if (usesOoxmlIndex) {
        const resolved = await DocumentReader.resolveParagraphForApply(
          paragraphIndexToUse ?? effectiveOoxmlIndex ?? undefined,
          applyResolveOptions,
        );
        if (resolved != null) {
          paragraphIndexToUse = resolved;
        }
      }

      const navigationAnchor =
        getModuleBNavigationAnchor(clause, featureCtx) || searchOriginal;
      if (usesOoxmlIndex && navigationAnchor) {
        await DocumentReader.navigateToParagraphByContent(navigationAnchor, {
          clauseId: clause.id,
          clauseNumber: clause.clause_number,
          strategy: 'undo_response',
          selectFullParagraph: !isTableRowClause(clause, featureCtx),
          ooxmlParagraphIndex:
            effectiveOoxmlIndex != null ? effectiveOoxmlIndex : undefined,
        });
      } else if (paragraphIndexToUse != null && !Number.isNaN(Number(paragraphIndexToUse))) {
        await DocumentReader.navigateToParagraph(Number(paragraphIndexToUse));
      } else if (searchOriginal) {
        await DocumentReader.navigateToParagraphByContent(searchOriginal);
      }

      let reverted = false;
      const tryRevertAtIndex = async (idx: number): Promise<boolean> => {
        try {
          return await RedlineInserter.revertTrackedChangesInParagraph(
            idx,
            revertContextToUse,
            beforeText,
          );
        } catch (wordErr) {
          console.error('[CrossCheckClauseCard] Word revert failed:', wordErr);
          throw wordErr;
        }
      };

      if (paragraphIndexToUse != null && !Number.isNaN(Number(paragraphIndexToUse))) {
        reverted = await tryRevertAtIndex(Number(paragraphIndexToUse));
        if (!reverted && usesOoxmlIndex && applyResolveOptions.searchText.length >= 15) {
          const retryResolved = await DocumentReader.resolveParagraphForApply(
            undefined,
            applyResolveOptions,
          );
          if (
            retryResolved != null &&
            retryResolved !== Number(paragraphIndexToUse)
          ) {
            reverted = await tryRevertAtIndex(retryResolved);
            if (reverted) {
              paragraphIndexToUse = retryResolved;
            }
          }
        }
      }

      if (!reverted) {
        setUndoNotFoundMessage("Couldn't find the changes to revert in the document. The card has been reset to Submit.");
      }

      // Always update card UI first (same as submit-to-response), then sync backend.
      onResponseUndone?.(clause.id);
      setCurrentEditedXml(null);
      setLocalAcceptedCrXml(null);

      if (subProjectId) {
        try {
          await apiService.updateClauseResponseSubmissionStatus(subProjectId, clause.id, false);
        } catch (backendErr) {
          console.warn(
            '[CrossCheckClauseCard] Undo response: backend status update failed (Word revert still applied):',
            backendErr,
          );
        }
      }
    } catch (err) {
      console.error('[CrossCheckClauseCard] Undo response failed:', err);
    } finally {
      setIsUndoing(false);
    }
  }, [
    isUndoing,
    isResponseSubmitted,
    clause,
    clientRequestPlainText,
    subProjectId,
    submitRevertContextForClause,
    onResponseUndone,
    featureCtx,
  ]);

  // Clear undo-not-found message when clause changes or after a delay
  React.useEffect(() => {
    if (!undoNotFoundMessage) return;
    const t = setTimeout(() => setUndoNotFoundMessage(null), 8000);
    return () => clearTimeout(t);
  }, [undoNotFoundMessage]);
  React.useEffect(() => setUndoNotFoundMessage(null), [clause.id]);

  const restoreCrEditSnapshot = React.useCallback(
    (el: HTMLElement, snapshot: CrEditHistorySnapshot) => {
      const baselineXml = crEditBaselineXmlRef.current;
      const displayXml = snapshot.previewXml ?? baselineXml;
      if (!displayXml) return;

      crEditPreviewXmlRef.current = snapshot.previewXml;

      const html = convertClientRequestXmlToHtml(displayXml);
      if (!html) return;

      crEditRefreshingRef.current = true;
      el.innerHTML = html;
      restoreAcceptedCaretOffset(el, snapshot.caret);
      crEditRefreshingRef.current = false;

      updateCrEditValue(snapshot.text);
      crEditLastAcceptedRef.current = snapshot.text;
      crEditExpectedCaretRef.current = snapshot.caret;

      const domAccepted = extractAcceptedTextFromHtmlElement(el);
      updateCrEditValue(domAccepted);
      crEditLastAcceptedRef.current = domAccepted;
    },
    [updateCrEditValue],
  );

  const performCrEditUndo = React.useCallback(() => {
    const el = crEditContentRef.current;
    if (!el || crEditUndoStackRef.current.length === 0) return;

    crEditRedoStackRef.current.push({
      text: crEditValueRef.current,
      previewXml: crEditPreviewXmlRef.current,
      caret: crEditExpectedCaretRef.current,
    });

    const snapshot = crEditUndoStackRef.current.pop()!;
    crEditRestoringRef.current = true;
    restoreCrEditSnapshot(el, snapshot);
    crEditRestoringRef.current = false;
  }, [restoreCrEditSnapshot]);

  const performCrEditRedo = React.useCallback(() => {
    const el = crEditContentRef.current;
    if (!el || crEditRedoStackRef.current.length === 0) return;

    crEditUndoStackRef.current.push({
      text: crEditValueRef.current,
      previewXml: crEditPreviewXmlRef.current,
      caret: crEditExpectedCaretRef.current,
    });

    const snapshot = crEditRedoStackRef.current.pop()!;
    crEditRestoringRef.current = true;
    restoreCrEditSnapshot(el, snapshot);
    crEditRestoringRef.current = false;
  }, [restoreCrEditSnapshot]);

  const refreshCrEditTrackChangesPreview = React.useCallback(
    (
      el: HTMLElement,
      accepted: string,
      caret: number,
      editSelectionHint: {
        start: number;
        end: number;
        insertedText: string;
      } | null = null,
    ) => {
      const baselineXml = crEditBaselineXmlRef.current;
      if (!baselineXml) return;

      const baselineAccepted = crEditBaselineAcceptedRef.current;
      const sourceXml = crEditPreviewXmlRef.current ?? baselineXml;
      // DOM-synced pre-keystroke text — same coordinate system as `accepted` and caret.
      const diffBaseline = crEditLastAcceptedRef.current || baselineAccepted;

      let previewXml = sourceXml;
      if (accepted !== diffBaseline) {
        try {
          // Validate the hint against the actual diff baseline. If the selection
          // range or its content drifted from the OOXML model (e.g. the DOM has
          // structural artifacts that change positions), fall back to LCS instead
          // of silently producing the wrong delete.
          const safeHint =
            editSelectionHint &&
            editSelectionHint.start >= 0 &&
            editSelectionHint.end > editSelectionHint.start &&
            editSelectionHint.end <= diffBaseline.length
              ? editSelectionHint
              : null;
          previewXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
            sourceXml,
            accepted,
            'User Edit',
            undefined,
            'char',
            {
              mode: 'simple-edit',
              simpleEditDiffStrategy: 'incremental-char',
              diffBaselineText: diffBaseline,
              editSelectionHint: safeHint ?? undefined,
            },
          );
        } catch (err) {
          console.error('[CrossCheckClauseCard] Live edit track-changes refresh failed:', err);
          return;
        }
      } else if (accepted === baselineAccepted) {
        previewXml = baselineXml;
      }

      crEditPreviewXmlRef.current = previewXml;

      const html = convertClientRequestXmlToHtml(previewXml);
      if (!html) return;

      crEditRefreshingRef.current = true;
      el.innerHTML = html;
      restoreAcceptedCaretOffset(el, caret);
      crEditExpectedCaretRef.current = caret;
      crEditRefreshingRef.current = false;

      // Keep crEditValueRef aligned with the DOM (preserves double-spaces),
      // not extractAcceptedText (which normalizes them).
      const domAccepted = extractAcceptedTextFromHtmlElement(el);
      updateCrEditValue(domAccepted);
      crEditLastAcceptedRef.current = domAccepted;
    },
    [updateCrEditValue],
  );

  const commitCrTrackChangesEdit = React.useCallback(
    (
      el: HTMLElement,
      inputType: string,
      inputData: string | null,
      source: 'beforeinput' | 'input',
      pendingSelection: { start: number; end: number } | null = null,
    ) => {
      const pendingAccepted = crEditPendingAcceptedRef.current;
      crEditPendingAcceptedRef.current = null;

      // currentValue must always be the PRE-keystroke text (the model state before the
      // browser applied this input). For selection deletes/replaces the DOM was already
      // mutated by the time `input` fires, so we use the keydown snapshot.
      // For everything else (typing, single backspace) crEditValueRef is the pre-state.
      const currentValue =
        pendingSelection && pendingAccepted != null
          ? pendingAccepted
          : crEditValueRef.current;

      const applied = applyTrackChangesEditInput(
        el,
        inputType,
        inputData,
        currentValue,
        crEditExpectedCaretRef.current,
        source === 'input',
        pendingSelection,
      );
      if (!applied) {
        return false;
      }

      if (!crEditRestoringRef.current) {
        pushCrEditUndo({
          text: currentValue,
          previewXml: crEditPreviewXmlRef.current,
          caret: crEditExpectedCaretRef.current,
        });
      }

      updateCrEditValue(applied.text);
      crEditExpectedCaretRef.current = applied.caret;

      // When the user replaced a selection
      // tell the diff generator the exact range + inserted text. Without this, LCS can
      // match incidental characters between the original selection and the inserted
      // text and produce a split delete / partial insert (e.g. typing "סעיף" over
      // "עוד יתווסף" keeps the original "ס" because both strings contain it).
      const hintInsertInputs = new Set([
        'insertText',
        'insertReplacementText',
        'insertFromPaste',
        'insertFromDrop',
      ]);
      const editSelectionHint =
        pendingSelection && pendingSelection.start !== pendingSelection.end
          ? {
              start: pendingSelection.start,
              end: pendingSelection.end,
              insertedText: hintInsertInputs.has(inputType) ? inputData ?? '' : '',
            }
          : null;

      refreshCrEditTrackChangesPreview(el, applied.text, applied.caret, editSelectionHint);

      return true;
    },
    [
      clause.id,
      pushCrEditUndo,
      refreshCrEditTrackChangesPreview,
      updateCrEditValue,
    ],
  );

  const handleCrTrackChangesEditInput = React.useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      if (crEditRefreshingRef.current) return;
      const inputEvent = e.nativeEvent as InputEvent;
      if (inputEvent.isComposing || crEditComposingRef.current) {
        updateCrEditValue(extractAcceptedTextFromHtmlElement(e.currentTarget));
        return;
      }

      if (crEditBeforeInputHandledRef.current) {
        crEditBeforeInputHandledRef.current = false;
        return;
      }

      if (isTrackChangesHistoryInputType(inputEvent.inputType)) {
        return;
      }

      const el = e.currentTarget;
      const pendingKey = crEditPendingKeyRef.current;
      crEditPendingKeyRef.current = null;
      const pendingSelection = crEditPendingSelectionRef.current;
      crEditPendingSelectionRef.current = null;
      const { inputType, data: inputData } = inferTrackChangesBeforeInput(inputEvent, pendingKey);

      if (isTrackChangesClipboardInputType(inputType)) return;

      // Let the browser apply the keystroke
      // then overwrite DOM with track-changes preview from our model.
      commitCrTrackChangesEdit(el, inputType, inputData, 'input', pendingSelection);
    },
    [clause.id, commitCrTrackChangesEdit, updateCrEditValue],
  );

  const handleCrTrackChangesCompositionEnd = React.useCallback(
    (e: React.CompositionEvent<HTMLDivElement>) => {
      crEditComposingRef.current = false;
      const el = e.currentTarget;
      const prevText = crEditValueRef.current;
      const accepted = extractAcceptedTextFromHtmlElement(el);
      const caret = saveAcceptedCaretOffset(el);
      if (!crEditRestoringRef.current && accepted !== prevText) {
        pushCrEditUndo({
          text: prevText,
          previewXml: crEditPreviewXmlRef.current,
          caret: crEditExpectedCaretRef.current,
        });
      }
      refreshCrEditTrackChangesPreview(el, accepted, caret);
    },
    [pushCrEditUndo, refreshCrEditTrackChangesPreview],
  );

  const handleCrTrackChangesCompositionStart = React.useCallback(() => {
    crEditComposingRef.current = true;
  }, []);

  const handleCrTrackChangesBeforeInput = React.useCallback(
    (e: React.FormEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      const inputEvent = e.nativeEvent as InputEvent;

      if (inputEvent.isComposing || crEditComposingRef.current) return;

      const inputType = inputEvent.inputType || '';
      if (isTrackChangesHistoryInputType(inputType)) {
        inputEvent.preventDefault();
        return;
      }
      // Keyboard edits: do not preventDefault — handled on `input` to avoid macOS error beep.
      if (!isTrackChangesClipboardInputType(inputType)) return;

      inputEvent.preventDefault();
      const applied = commitCrTrackChangesEdit(
        el,
        inputType,
        inputEvent.data,
        'beforeinput',
      );
      if (applied) {
        crEditBeforeInputHandledRef.current = true;
      }
    },
    [clause.id, commitCrTrackChangesEdit],
  );

  const recordCrEditUserNavigation = React.useCallback((el: HTMLElement) => {
    if (crEditRefreshingRef.current) return;
    crEditExpectedCaretRef.current = saveAcceptedCaretOffset(el);
  }, []);

  const handleCrTrackChangesPointerDown = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      // Capture click intent before Office WebView applies a stale caret on keydown.
      requestAnimationFrame(() => {
        if (crEditContentRef.current === e.currentTarget) {
          recordCrEditUserNavigation(e.currentTarget);
        }
      });
    },
    [recordCrEditUserNavigation],
  );

  const handleCrTrackChangesPointerUp = React.useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      recordCrEditUserNavigation(e.currentTarget);
    },
    [recordCrEditUserNavigation],
  );

  const handleCrTrackChangesSelect = React.useCallback(
    (e: React.SyntheticEvent<HTMLDivElement>) => {
      recordCrEditUserNavigation(e.currentTarget);
    },
    [recordCrEditUserNavigation],
  );

  const handleCrTrackChangesKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'z' || e.key === 'Z')) {
        e.preventDefault();
        if (e.shiftKey) {
          performCrEditRedo();
        } else {
          performCrEditUndo();
        }
        return;
      }
      if (mod && e.key === 'y') {
        e.preventDefault();
        performCrEditRedo();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        crEditPendingKeyRef.current = e.key;
        const el = crEditContentRef.current;
        if (el) {
          const range = saveAcceptedSelectionRange(el);
          crEditPendingSelectionRef.current =
            range.start !== range.end ? range : null;
          // DOM coords — paired with pendingSelection so the delete substring matches.
          crEditPendingAcceptedRef.current = getTrackChangesEditCurrentText(
            el,
            crEditValueRef.current,
          );
        }
      } else if (e.key.length === 1) {
        crEditPendingKeyRef.current = e.key;
        const el = crEditContentRef.current;
        if (el) {
          const range = saveAcceptedSelectionRange(el);
          if (range.start !== range.end) {
            // Selection-at-keydown snapshot — browser collapses before `input` fires.
            crEditPendingSelectionRef.current = range;
            crEditPendingAcceptedRef.current = getTrackChangesEditCurrentText(
              el,
              crEditValueRef.current,
            );
          } else {
            crEditPendingSelectionRef.current = null;
            crEditPendingAcceptedRef.current = null;
          }
        }
      } else if (
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'Home' ||
        e.key === 'End'
      ) {
        requestAnimationFrame(() => {
          if (crEditContentRef.current) {
            recordCrEditUserNavigation(crEditContentRef.current);
          }
        });
      }
    },
    [performCrEditRedo, performCrEditUndo],
  );

  React.useEffect(() => {
    if (!editingCr || crEditMode !== 'track-changes') return;

    const onSelectionChange = () => {
      const el = crEditContentRef.current;
      if (!el) return;
      const sel = window.getSelection();
      if (!sel?.rangeCount || !el.contains(sel.anchorNode)) return;
      if (crEditRefreshingRef.current) return;
      crEditExpectedCaretRef.current = saveAcceptedCaretOffset(el);
    };

    document.addEventListener('selectionchange', onSelectionChange);
    return () => document.removeEventListener('selectionchange', onSelectionChange);
  }, [editingCr, crEditMode]);

  React.useEffect(() => {
    if (!editingCr || crEditMode !== 'track-changes' || !crEditContentRef.current) {
      crEditSessionInitRef.current = false;
      return;
    }
    if (crEditSessionInitRef.current) return;

    const initialXml =
      displayXml ||
      buildAiSuggestionPreviewXml(clientRequestXml, clientRequestPlainText, displayedClientRequestPlainText);
    const html = initialXml ? convertClientRequestXmlToHtml(initialXml) : null;
    if (html) {
      crEditContentRef.current.innerHTML = html;
    }
    crEditSessionInitRef.current = true;
    // Re-sync baseline & current text from the DOM so coordinates match
    // saveAcceptedSelectionRange (DOM preserves double-spaces; extractAcceptedText collapses them).
    const domBaseline = extractAcceptedTextFromHtmlElement(crEditContentRef.current);
    if (domBaseline !== crEditBaselineAcceptedRef.current) {
      crEditBaselineAcceptedRef.current = domBaseline;
      updateCrEditValue(domBaseline);
    }
    const initialCaret = domBaseline.length;
    crEditExpectedCaretRef.current = initialCaret;
    resetCrEditHistory();
    const baselineAccepted = crEditBaselineAcceptedRef.current;
    crEditLastAcceptedRef.current = baselineAccepted;
    requestAnimationFrame(() => {
      const el = crEditContentRef.current;
      if (!el) return;
      el.focus();
      restoreAcceptedCaretOffset(el, initialCaret);
      crEditExpectedCaretRef.current = initialCaret;
    });
  }, [
    editingCr,
    crEditMode,
    displayXml,
    displayedClientRequestPlainText,
    clientRequestXml,
    clientRequestPlainText,
    resetCrEditHistory,
    updateCrEditValue,
  ]);

  const normalizeCrEditText = (value: string): string => value.replace(/\s+/g, ' ').trim();

  // ─── Save CR edit ────────────────────────────────────────────────────────────
  const handleSaveCrEdit = async () => {
    let rawText = crEditValueRef.current;
    let saveOutcome: {
      changed: boolean;
      savedXmlLen: number;
      savedTo: 'localAcceptedCrXml' | 'currentEditedXml' | 'cleared' | 'none';
    } = { changed: false, savedXmlLen: 0, savedTo: 'none' };
    if (crEditMode === 'track-changes' && crEditContentRef.current) {
      const domAccepted = extractAcceptedTextFromHtmlElement(crEditContentRef.current);
      const baseline = crEditBaselineAcceptedRef.current;
      if (domAccepted !== baseline && rawText === baseline) {
        rawText = domAccepted;
        updateCrEditValue(domAccepted);
      }
    }

    // Compare normalized for change detection
    // char positions align with the OOXML accepted-text baseline (no whitespace collapse).
    const editedNorm = normalizeCrEditText(rawText);
    const baselineRaw =
      crEditBaselineAcceptedRef.current ||
      (displayXml ? TrackChangesParser.extractAcceptedText(displayXml) : displayedClientRequestPlainText);
    const baselineNorm = normalizeCrEditText(baselineRaw);

    if (editedNorm !== baselineNorm) {
      saveOutcome.changed = true;
      const originalNorm = normalizeCrEditText(clientRequestPlainText);

      if (editedNorm === originalNorm) {
        handleClearAiSuggestion();
        setLocalAcceptedCrXml(null);
        saveOutcome.savedTo = 'cleared';
      } else if (editedNorm) {
        try {
          const { RobustXmlTrackChangesGenerator } = await import(
            '../../features/rephrasing/RobustXmlTrackChangesGenerator'
          );

          let resultXml: string | null = null;

          // User edits run in simple-edit mode: behave like a human typing into a Word
          // document with track changes on. Our-side pending insertions shrink silently;
          // counterparty / original text deletions produce proper w:del entries.
          if (crEditMode === 'track-changes') {
            const fallbackXml =
              clientRequestXml ||
              TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);

            const previewXml = crEditPreviewXmlRef.current;
            let previewAccepted = '';
            if (previewXml) {
              try {
                previewAccepted = TrackChangesParser.extractAcceptedText(previewXml);
              } catch {
                previewAccepted = '';
              }
            }

            // Preview XML is the OOXML built incrementally per keystroke during live edit
            // (correct track-changes structure: preserves AI insertions/deletions and adds
            // surgical User Edit blocks). Prefer it whenever its accepted text matches
            // what the user sees — compare normalized because extractAcceptedText collapses
            // whitespace while the DOM walker preserves it.
            const previewMatches =
              !!previewXml &&
              (previewAccepted === rawText ||
                normalizeCrEditText(previewAccepted) === normalizeCrEditText(rawText));

            if (previewMatches) {
              resultXml = previewXml;
            } else if (currentEditedXml) {
              const baselineXml =
                crEditBaselineXmlRef.current || currentEditedXml || displayXml || fallbackXml;
              resultXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
                baselineXml,
                rawText,
                'User Edit',
                undefined,
                'char',
                { mode: 'simple-edit' },
              );
            } else {
              const baselineXml = crEditBaselineXmlRef.current || displayXml || fallbackXml;
              resultXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
                baselineXml,
                rawText,
                'User Edit',
                undefined,
                'char',
                { mode: 'simple-edit' },
              );
            }

            if (currentEditedXml) {
              setCurrentEditedXml(resultXml);
              saveOutcome.savedTo = 'currentEditedXml';
            } else {
              setLocalAcceptedCrXml(resultXml);
              saveOutcome.savedTo = 'localAcceptedCrXml';
            }
            saveOutcome.savedXmlLen = resultXml?.length ?? 0;
          } else {
            const baselineXml =
              clientRequestXml || TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
            resultXml = RobustXmlTrackChangesGenerator.generateTrackChangesXml(
              baselineXml,
              rawText,
              'User Edit',
              undefined,
              'word',
              { mode: 'simple-edit' },
            );
            if (currentEditedXml) {
              setCurrentEditedXml(resultXml);
              saveOutcome.savedTo = 'currentEditedXml';
            } else {
              setLocalAcceptedCrXml(resultXml);
              saveOutcome.savedTo = 'localAcceptedCrXml';
            }
            saveOutcome.savedXmlLen = resultXml?.length ?? 0;
          }

        } catch (e) {
          console.error('[CrossCheckClauseCard] Save CR edit failed:', e);
        }
      }
    }

    crEditBaselineXmlRef.current = null;
    crEditBaselineAcceptedRef.current = '';
    crEditSessionInitRef.current = false;
    resetCrEditHistory();
    setEditingCr(false);
    setCrEditMode('plain');
  };

  const handleCancelCrEdit = React.useCallback(() => {
    crEditBaselineXmlRef.current = null;
    crEditBaselineAcceptedRef.current = '';
    crEditLastAcceptedRef.current = '';
    crEditSessionInitRef.current = false;
    resetCrEditHistory();
    setEditingCr(false);
    setCrEditMode('plain');
  }, [clause.id, resetCrEditHistory]);

  const hasBocEdits = (clause.edit_history?.bank_of_changes?.length ?? 0) > 0;

  const performBocSave = React.useCallback(async (
    newValue: string,
    isNewOption: boolean,
    selectedIndex: number,
  ): Promise<boolean> => {
    if (!subProjectId) return false;
    const trimmed = newValue.trim();
    if (!trimmed) return false;

    setSavingBoc(true);
    try {
      const response = await apiService.editClause(subProjectId, {
        clause_id: clause.id,
        field: 'bank_of_changes',
        new_value: trimmed,
        is_new_option: isNewOption,
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to save changes');
      }

      const updated = applyBocEditLocalUpdate(clause, trimmed, isNewOption, selectedIndex);
      onClauseEdited?.(updated);

      if (isNewOption) {
        const opts = getBocOptions(updated);
        if (opts && opts.length > 0) {
          setSelectedBocIndex(opts.length - 1);
        }
      }

      onActionBanner?.({ type: 'success', message: 'Changes saved successfully' });
      setPendingBrainShare({ newValue: trimmed, isNewOption });
      setShowBrainShareModal(true);
      return true;
    } catch (err) {
      console.error('Error saving Bank of Changes edit:', err);
      onActionBanner?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to save changes',
      });
      return false;
    } finally {
      setSavingBoc(false);
    }
  }, [clause, subProjectId, onClauseEdited, onActionBanner]);

  // ─── Save BoC edit ───────────────────────────────────────────────────────────
  const handleSaveBocEdit = async () => {
    const trimmed = bocEditValue.trim();
    if (!trimmed) return;
    if (trimmed === displayBocText.trim()) {
      setEditingBoc(false);
      return;
    }
    const ok = await performBocSave(trimmed, false, safeSelectedBocIndex);
    if (ok) setEditingBoc(false);
  };

  const handleAddBoc = async () => {
    const trimmed = bocNewValue.trim();
    if (!trimmed) {
      setAddingBoc(false);
      return;
    }
    const opts = getBocOptions(clause);
    const nextIndex = opts?.length ?? 0;
    const ok = await performBocSave(trimmed, true, nextIndex);
    if (ok) {
      setBocNewValue('');
      setAddingBoc(false);
    }
  };

  // ─── BoC multi-select helpers ────────────────────────────────────────────────

  const handleBocCheckboxChange = React.useCallback((idx: number) => {
    setBocCheckedIndices(prev => {
      const next = prev.includes(idx) ? prev.filter(i => i !== idx) : [...prev, idx];
      // Rebuild combined text based on new selection order
      if (next.length > 1 && bocOptions) {
        setBocCombinedText(next.map(i => bocOptions[i]?.text ?? '').filter(Boolean).join('\n\n'));
      }
      return next;
    });
  }, [bocOptions]);

  const handleBocApply = React.useCallback(async () => {
    if (bocCheckedIndices.length === 0) return;
    if (bocCheckedIndices.length === 1) {
      // Single selection via checkbox: same as click — just select locally and collapse
      setSelectedBocIndex(bocCheckedIndices[0]);
      handleClearAiSuggestion();
      setBocExpanded(false);
      setBocCheckedIndices([]);
      setBocCombinedText('');
      return;
    }
    // Multi: save combined text as a new option (not overwriting any existing one)
    const trimmed = bocCombinedText.trim();
    if (!trimmed) return;
    const ok = await performBocSave(trimmed, true, (bocOptions?.length ?? 0));
    if (ok) {
      setBocCheckedIndices([]);
      setBocCombinedText('');
      setBocExpanded(false);
    }
  }, [bocCheckedIndices, bocCombinedText, safeSelectedBocIndex, performBocSave, handleClearAiSuggestion]);

  const handleBrainShareConfirm = async () => {
    if (!pendingBrainShare || !subProjectId) {
      setShowBrainShareModal(false);
      setPendingBrainShare(null);
      return;
    }
    setSharingBrainShare(true);
    try {
      const response = await apiService.editClause(subProjectId, {
        clause_id: clause.id,
        field: 'bank_of_changes',
        new_value: pendingBrainShare.newValue,
        is_new_option: pendingBrainShare.isNewOption,
        share_to_brainshare: true,
      });
      if (!response.success) {
        throw new Error(response.message || 'Failed to share to BrainShare');
      }
      onActionBanner?.({ type: 'success', message: 'Shared to BrainShare successfully!' });
      onBrainShareUpdated?.();
    } catch (err) {
      console.error('Error sharing to BrainShare:', err);
      onActionBanner?.({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to share to BrainShare',
      });
    } finally {
      setSharingBrainShare(false);
      setShowBrainShareModal(false);
      setPendingBrainShare(null);
    }
  };

  const handleBrainShareCancel = () => {
    setShowBrainShareModal(false);
    setPendingBrainShare(null);
  };

  const cardColor = clause.color || '#3b82f6';

  const canGoTo = clause.paragraph_index != null || (clientRequestPlainText != null && clientRequestPlainText.trim().length > 0);

  const handleGoToParagraph = React.useCallback(async () => {
    if (goToInFlightRef.current) {
      return;
    }
    goToInFlightRef.current = true;
    const startedAt = Date.now();
    try {
    const paragraphIndex = resolveEffectiveParagraphIndex(clause);
    const isTableRow = isTableRowClause(clause, featureCtx);
    const selectFullParagraph = !isTableRow;
    const moduleBAnchor = getModuleBNavigationAnchor(clause, featureCtx) || clientRequestPlainText?.trim() || '';

    const navContext = {
      clauseId: clause.id,
      clauseNumber: clause.clause_number,
    };
    const navSearchOptions = { selectFullParagraph };

    const tryContentNavigation = async (source: string, text: string): Promise<boolean> => {
      if (!text || text.trim().length < 15) return false;
      return DocumentReader.navigateToParagraphByContent(text, {
        ...navContext,
        ...navSearchOptions,
        strategy: 'content_search',
        candidateSource: source,
        maxSearchAttempts: 3,
        ooxmlParagraphIndex:
          paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))
            ? Number(paragraphIndex)
            : undefined,
      });
    };

    // Table rows / Module B: search by TC request text before trusting stored indices.
    if (isTableRow || clause.module_type === 'module_b') {
      if (moduleBAnchor.length >= 15 && (await tryContentNavigation('module_b_anchor', moduleBAnchor))) {
        return;
      }

      if (isTableRow) {
        const seen = new Set<string>();
        for (const [i, text] of getTableRowSearchAnchors(clause, featureCtx).entries()) {
          const key = text.replace(/\s+/g, ' ').trim();
          if (!key || seen.has(key)) continue;
          seen.add(key);
          if (await tryContentNavigation(`table_row_anchor_${i + 1}`, text)) return;
        }
      }
    }

    // Stored OOXML index — direct paragraph select, then short search fallback.
    if (paragraphIndex != null && !Number.isNaN(Number(paragraphIndex))) {
      const navigated = await DocumentReader.navigateToDocumentParagraphIndex(
        Number(paragraphIndex),
        { ...navContext, ...navSearchOptions, strategy: 'ooxml_paragraph_index' },
        moduleBAnchor || undefined,
      );
      if (navigated) {
        return;
      }
    }

    const fallbackTexts = [
      getClientRequestSearchText(clause, featureCtx),
      clientRequestPlainText?.trim() || '',
      clause.original_text?.trim() || '',
    ];
    const seenFallback = new Set<string>();
    for (const text of fallbackTexts) {
      const key = text.replace(/\s+/g, ' ').trim();
      if (!key || seenFallback.has(key)) continue;
      seenFallback.add(key);
      if (key === moduleBAnchor.replace(/\s+/g, ' ').trim()) continue;
      if (await tryContentNavigation('fallback', text)) return;
    }

    debugWarn('CrossCheckClauseCard: go-to all strategies failed', {
      ...navContext,
      paragraphIndex: paragraphIndex ?? null,
      elapsedMs: Date.now() - startedAt,
      isTableRow,
    });
    } finally {
      goToInFlightRef.current = false;
    }
  }, [clause, clientRequestPlainText, featureCtx]);

  /** Jump to existing clause: SA PDF panel when available, else Word document search. */
  const handleGoToInsightExistingClause = React.useCallback(async (insight: CrossCheckInsight) => {
    const pi = getInsightExistingClauseParagraphIndex(insight);
    if (pi != null) {
      const navigated = await DocumentReader.navigateToParagraph(pi);
      if (navigated) return;
      console.warn('[CrossCheckClauseCard] Go to existing clause by index failed:', { pi });
    }
    const searchText = getInsightExistingClauseSearchText(insight);
    if (searchText) {
      await DocumentReader.navigateToParagraphByContent(searchText, {
        selectFullParagraph: true,
      });
    }
  }, []);

  const handleInsightGoTo = React.useCallback(
    (insight: CrossCheckInsight) => {
      const existingClauseText = getInsightExistingClauseSearchText(insight);
      if (onGoToSalesAgreementSource && existingClauseText) {
        onGoToSalesAgreementSource(clause, existingClauseText);
        setShowInsightModal(false);
        return;
      }
      void handleGoToInsightExistingClause(insight);
    },
    [onGoToSalesAgreementSource, clause, handleGoToInsightExistingClause],
  );

  const handleViewInAgreementConfirmChoice = React.useCallback(
    (viewInAgreement: boolean) => {
      setShowViewInAgreementConfirm(false);
      if (viewInAgreement) {
        const insights = clause.insights ?? [];
        const insight = pickInsightForSalesAgreementGoTo(insights);
        if (insight) {
          handleInsightGoTo(insight);
        }
      }
      void runAlreadyExistAiFlow();
    },
    [clause.insights, handleInsightGoTo, runAlreadyExistAiFlow],
  );

  const canShowInsightGoTo = React.useCallback(
    (insight: CrossCheckInsight) =>
      Boolean(onGoToSalesAgreementSource && getInsightExistingClauseSearchText(insight)) ||
      canNavigateToInsightExistingClause(insight),
    [onGoToSalesAgreementSource],
  );

  const renderSubmitToResponseRow = () => (
    <>
      <div className="cc-submit-row cc-submit-row--client-request">
        {isResponseSubmitted ? (
          <button
            className={`cc-submit-btn cc-undo-response${isUndoing ? ' cc-undoing' : ''}`}
            disabled={isUndoing || isSubmitting}
            onClick={handleUndoResponse}
          >
            {isUndoing ? (
              <>
                <div className="cc-submit-spinner" />
                Undoing…
              </>
            ) : (
              <>
                <svg className="cc-submit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M3 10h10a5 5 0 015 5v2M3 10l4-4m-4 4l4 4" />
                </svg>
                Undo Response
              </>
            )}
          </button>
        ) : (
          <button
            className={`cc-submit-btn${submitResult === 'success' ? ' cc-submit-ok' : ''}${submitResult === 'error' ? ' cc-submit-err' : ''}`}
            disabled={isSubmitting || isUndoing}
            onClick={handleSubmitToResponse}
          >
            {isSubmitting ? (
              <>
                <div className="cc-submit-spinner" />
                Applying…
              </>
            ) : submitResult === 'success' ? (
              <>✓ Applied to Document</>
            ) : submitResult === 'error' ? (
              <>⚠ Could not apply</>
            ) : (
              <>
                <svg className="cc-submit-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Submit to Response
              </>
            )}
          </button>
        )}
      </div>
      {undoNotFoundMessage && (
        <div className="cc-undo-not-found-msg" role="alert">
          {undoNotFoundMessage}
        </div>
      )}
    </>
  );

  return (
    <>
      <div
        className={`cc-clause-card${isLocked ? ' cc-locked' : ''}`}
        style={{ borderLeftColor: cardColor }}
      >
        {/* ── Header (clickable = go to paragraph in document) ─────────── */}
        <div
          ref={cardHeaderRef}
          className={`cc-clause-header${canGoTo ? ' cc-clause-header-clickable' : ''}`}
          role={canGoTo ? 'button' : undefined}
          tabIndex={canGoTo ? 0 : undefined}
          onClick={canGoTo ? handleGoToParagraph : undefined}
          onKeyDown={canGoTo ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleGoToParagraph(); } } : undefined}
          title={canGoTo ? 'Go to paragraph in document' : undefined}
        >
          <div className="cc-clause-header-left">
            <h4 className="cc-clause-title">
              Clause {clauseNumberDisplay || index + 1}
            </h4>
            <span className={`cc-clause-badge ${badge.className}`}>{badge.label}</span>
          </div>
          <div className="cc-clause-header-actions" onClick={(e) => e.stopPropagation()}>
            <button
              className={`cc-action-btn${clause.verification_status?.verified ? ' cc-verified' : ''}`}
              title={clause.verification_status?.verified ? 'Verified' : 'Not verified'}
            >
              <svg className="cc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
            <button
              type="button"
              className={`cc-action-btn${isLocked ? ' cc-locked-btn' : ''}${isUpdatingLock ? ' cc-action-btn-busy' : ''}`}
              title={isLocked ? 'Unlock clause' : 'Lock clause'}
              disabled={isUpdatingLock}
              onClick={(e) => {
                e.stopPropagation();
                onToggleLock?.(clause);
              }}
            >
              {isUpdatingLock ? (
                <span className="cc-spinner-small" aria-hidden />
              ) : isLocked ? (
                <svg className="cc-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="cc-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                </svg>
              )}
            </button>

            {showKebabMenu && (
              <ClauseCardKebabMenu
                clause={clause}
                isMutating={isMutating}
                isLocked={!!isLocked}
                isClauseSubmitted={isClauseSubmitted}
                moveBlockedMessage={moveBlockedMessage}
                isMenuOpen={isMenuOpen}
                isMoveSubmenuOpen={isMoveSubmenuOpen}
                moveSubmenuPosition={moveSubmenuPosition}
                menuRef={menuRef}
                moveMenuItemRef={moveMenuItemRef}
                moveSubmenuRef={moveSubmenuRef}
                kebabVariant={kebabVariant}
                onMoveClause={onMoveClause}
                onMoveClauseMatter={onMoveClauseMatter}
                onAnalyzeAgain={onAnalyzeAgain}
                onAskAgent={onAskAgent}
                onToggleMenu={() => {
                  setIsMenuOpen((prev) => !prev);
                  setIsMoveSubmenuOpen(false);
                }}
                onToggleMoveSubmenu={() => {
                  if (!isClauseSubmitted) {
                    setIsMoveSubmenuOpen((prev) => !prev);
                  }
                }}
                onCloseMenu={() => {
                  setIsMenuOpen(false);
                  setIsMoveSubmenuOpen(false);
                }}
              />
            )}
          </div>
        </div>

        {/* ── Body: collapsed when locked (matches dashboard) ───────────────── */}
        {isLocked ? (
          <div className="cc-clause-locked-message">
            <span className="cc-clause-locked-text">
              <span className="cc-clause-locked-icon" aria-hidden>🔒</span>
              Clause is locked - click unlock to view content
            </span>
            {clause.verification_status?.verified && (
              <span className="cc-clause-verified-badge">
                <svg className="cc-verified-icon" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verified
              </span>
            )}
          </div>
        ) : (
        <div className="cc-clause-body">
          {/* Submission chips */}
          {(clause.submission_status?.schedule_submitted || clause.submission_status?.response_submitted) && (
            <div className="cc-status-badges">
              {clause.submission_status.schedule_submitted && (
                <span className="cc-status-chip cc-schedule">Schedule Submitted</span>
              )}
              {clause.submission_status.response_submitted && (
                <span className="cc-status-chip cc-response">Response Submitted</span>
              )}
            </div>
          )}

          {/* ── CLIENT REQUEST ─────────────────────────────────────── */}
          <div className="cc-section">
            <div className="cc-section-header cc-client-request-header">
              <div className="cc-client-request-header-inner">
                <div className="cc-section-label-row">
                  <span className="cc-section-label">Client Request</span>
                  {clause.client_request_source && CLIENT_REQUEST_SOURCE_LABELS[clause.client_request_source] && (
                    <span
                      className={`cc-source-chip cc-source-${clause.client_request_source}`}
                      title={`Client request source: ${CLIENT_REQUEST_SOURCE_LABELS[clause.client_request_source]}`}
                    >
                      {CLIENT_REQUEST_SOURCE_LABELS[clause.client_request_source]}
                    </span>
                  )}
                  {hasInsights && (
                    <button
                      type="button"
                      className="cc-insight-circle"
                      onClick={() => setShowInsightModal(true)}
                      title={`${clause.insights!.length} insight${clause.insights!.length > 1 ? 's' : ''} available - click to view`}
                    >
                      <svg className="cc-insight-circle-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M11 3a1 1 0 10-2 0v1a1 1 0 102 0V3zM15.657 5.757a1 1 0 00-1.414-1.414l-.707.707a1 1 0 001.414 1.414l.707-.707zM18 10a1 1 0 01-1 1h-1a1 1 0 110-2h1a1 1 0 011 1zM5.05 6.464A1 1 0 106.464 5.05l-.707-.707a1 1 0 00-1.414 1.414l.707.707zM5 10a1 1 0 01-1 1H3a1 1 0 110-2h1a1 1 0 011 1zM8 16v-1h4v1a2 2 0 11-4 0zM12 14c.015-.34.208-.646.477-.859a4 4 0 10-4.954 0c.27.213.462.519.476.859h4.002z" />
                      </svg>
                    </button>
                  )}
                </div>
                {(clause.track_changes_interpretation ?? primaryInsight?.explanation) && (
                  <div className="cc-explanation-row">
                    <button
                      type="button"
                      className="cc-explanation-pill"
                      onClick={() => setShowExplanationModal(true)}
                      title="Click to view track changes interpretation"
                    >
                      <svg className="cc-explanation-pill-icon" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                      Interpretation
                    </button>
                  </div>
                )}
              </div>
            </div>

            <div
              className={`cc-client-request-content${isStreamingEffective ? ' cc-client-request-content--ai-processing' : ''}`}
              dir={clientRequestDir}
            >
              {editingCr ? (
                <div className="cc-inline-edit">
                  {crEditMode === 'track-changes' ? (
                    <div
                      className="cc-client-request-content cc-inline-edit-tc"
                      dir={clientRequestDir}
                    >
                      <div className="cc-paragraph-xml-display">
                        <div
                          ref={crEditContentRef}
                          className="cc-selected-text-xml cc-selected-text-xml--editable"
                          contentEditable
                          suppressContentEditableWarning
                          onBeforeInput={handleCrTrackChangesBeforeInput}
                          onInput={handleCrTrackChangesEditInput}
                          onCompositionStart={handleCrTrackChangesCompositionStart}
                          onCompositionEnd={handleCrTrackChangesCompositionEnd}
                          onKeyDown={handleCrTrackChangesKeyDown}
                          onMouseDown={handleCrTrackChangesPointerDown}
                          onMouseUp={handleCrTrackChangesPointerUp}
                          onSelect={handleCrTrackChangesSelect}
                          dir={clientRequestDir}
                        />
                      </div>
                    </div>
                  ) : (
                    <textarea
                      className="cc-edit-textarea"
                      value={crEditValue}
                      onChange={e => {
                        const next = e.target.value;
                        updateCrEditValue(next);
                        crEditLastAcceptedRef.current = next;
                      }}
                      dir={clientRequestDir}
                      rows={4}
                      autoFocus
                    />
                  )}
                  <div className="cc-inline-edit-actions">
                    <button className="cc-btn cc-btn-save" onClick={() => void handleSaveCrEdit()}>Save</button>
                    <button className="cc-btn cc-btn-cancel" onClick={handleCancelCrEdit}>Cancel</button>
                  </div>
                </div>
              ) : isStreamingEffective ? (
                clientRequestDisplayHtml || clientRequestXml ? (
                  <ClientRequestXmlPreview html={clientRequestDisplayHtml} xml={clientRequestXml} dir={clientRequestDir} />
                ) : (
                  <p
                    className={`cc-section-text${!clientRequestPlainText ? ' cc-empty' : ''}`}
                    dir={clientRequestDir}
                  >
                    {clientRequestPlainText || 'No client request'}
                  </p>
                )
              ) : displayXml || effectivePreviewHtml ? (
                <ClientRequestXmlPreview html={effectivePreviewHtml} xml={displayXml} dir={clientRequestDir} />
              ) : (
                <p
                  className={`cc-section-text${!clientRequestPlainText ? ' cc-empty' : ''}`}
                  dir={clientRequestDir}
                >
                  {clientRequestPlainText || 'No client request'}
                </p>
              )}
            </div>

            {/* AI + Edit (AI = bank-of-changes or past-agreement source) */}
            {!editingCr && (
              <div className="cc-action-row">
                <div className="cc-ai-btn-wrapper" ref={aiMenuRef}>
                  <button
                    className="cc-btn cc-btn-ai"
                    onClick={() => {
                      if (!hasBocForAi && !hasPaForAi) return;
                      if (hasBocForAi && hasPaForAi) {
                        setAiSourceMenuOpen((o) => !o);
                      } else {
                        handleAiSuggestionFrom(hasBocForAi ? 'bank_of_changes' : 'past_agreement');
                      }
                    }}
                    disabled={isStreamingEffective || (!hasBocForAi && !hasPaForAi) || !resolvedProjectId}
                    title={
                      hasBocForAi && hasPaForAi
                        ? 'Choose reference source for AI suggestion'
                        : hasPaForAi
                        ? 'Generate suggestion from Past negotiations'
                        : 'Generate suggestion from Bank of Changes'
                    }
                  >
                    <span className="cc-btn-star">✦</span> AI
                    {hasBocForAi && hasPaForAi && (
                      <svg className="cc-ai-chevron" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                  {aiSourceMenuOpen && hasBocForAi && hasPaForAi && (
                    <div className="cc-ai-source-menu">
                      <button
                        className="cc-ai-source-item"
                        onClick={() => { setAiSourceMenuOpen(false); handleAiSuggestionFrom('bank_of_changes'); }}
                      >
                        <span className="cc-ai-source-icon">📋</span>
                        <span>
                          <strong>Bank of changes</strong>
                          <span className="cc-ai-source-hint">Use selected BoC option</span>
                        </span>
                      </button>
                      <button
                        className="cc-ai-source-item"
                        onClick={() => { setAiSourceMenuOpen(false); handleAiSuggestionFrom('past_agreement'); }}
                      >
                        <span className="cc-ai-source-icon">📜</span>
                        <span>
                          <strong>Past negotiations</strong>
                          <span className="cc-ai-source-hint">Use selected past contract clause</span>
                        </span>
                      </button>
                    </div>
                  )}
                </div>
                <button
                  className="cc-btn cc-btn-edit"
                  onClick={() => {
                    const baselineXml =
                      displayXml ||
                      clientRequestXml ||
                      TrackChangesParser.createMinimalParagraphOoxml(clientRequestPlainText);
                    crEditBaselineXmlRef.current = baselineXml;
                    try {
                      crEditBaselineAcceptedRef.current = TrackChangesParser.extractAcceptedText(baselineXml);
                    } catch {
                      crEditBaselineAcceptedRef.current = displayedClientRequestPlainText;
                    }
                    updateCrEditValue(crEditBaselineAcceptedRef.current);
                    crEditLastAcceptedRef.current = crEditBaselineAcceptedRef.current;
                    crEditExpectedCaretRef.current = crEditBaselineAcceptedRef.current.length;
                    crEditPreviewXmlRef.current = null;
                    resetCrEditHistory();
                    setCrEditMode(canEditWithTrackChangesPreview ? 'track-changes' : 'plain');
                    setEditingCr(true);
                  }}
                  title={hasAiSuggestion ? 'Edit AI suggestion' : 'Edit client request'}
                >
                  <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                  Edit
                </button>
                {canAcceptTrackChanges && (
                  <button
                    type="button"
                    className="cc-btn cc-btn-accept"
                    onClick={handleAcceptClientRequestTrackChanges}
                    disabled={isStreamingEffective || isSubmitting}
                    title="Accept track changes in Word and submit this clause to response"
                  >
                    <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {isSubmitting ? 'Accepting…' : 'Accept'}
                  </button>
                )}
                {(hasAiSuggestion || localAcceptedCrXml || isStreamingEffective) && (
                  <button
                    type="button"
                    className="cc-btn cc-btn-clear"
                    onClick={handleClearAiSuggestion}
                    title={
                      hasAiSuggestion
                        ? 'Clear AI suggestion and restore original client request'
                        : 'Clear edits and restore original client request'
                    }
                  >
                    <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Clear
                  </button>
                )}
              </div>
            )}

            {!editingCr && !hasInsights && renderSubmitToResponseRow()}

            {/* AI - Already Exist: only when an existing clause was detected (same condition as the insight circle icon) */}
            {!editingCr && hasInsights && (
              <button
                className="cc-btn-ai-exist"
                onClick={handleAlreadyExistAI}
                disabled={isStreamingEffective || !subProjectId}
                title="Generate AI suggestion based on existing clause in Sales Agreement"
              >
                <svg className="cc-btn-bolt" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                </svg>
                AI - Already Exist
              </button>
            )}

            {!editingCr && hasInsights && renderSubmitToResponseRow()}
          </div>

          {/* ── BANK OF CHANGES ─────────────────────────────────────── */}
          {bocText && (
            <div className="cc-section">
              <div className="cc-section-header">
                <div className="cc-boc-header-left">
                  <span className="cc-section-label">Bank of Changes</span>
                  {hasBocEdits && (
                    <span className="cc-edited-badge" title="Manually edited">(edited)</span>
                  )}
                  {bocCount > 1 && (
                    <button
                      className="cc-boc-badge"
                      onClick={() => setBocExpanded(!bocExpanded)}
                      title={bocExpanded ? 'Collapse options' : `Show all ${bocCount} options`}
                    >
                      {bocCount}
                    </button>
                  )}
                </div>
                <button
                  className="cc-chevron-btn"
                  onClick={() => setBocExpanded(!bocExpanded)}
                >
                  <svg
                    className={`cc-chevron-icon${bocExpanded ? ' cc-open' : ''}`}
                    viewBox="0 0 20 20" fill="currentColor"
                  >
                    <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>

              {editingBoc ? (
                <div className="cc-inline-edit">
                  <textarea
                    ref={bocEditTextareaRef}
                    className="cc-edit-textarea cc-edit-textarea-auto"
                    value={bocEditValue}
                    onChange={(e) => setBocEditValue(e.target.value)}
                    dir={getDir(bocEditValue)}
                    rows={1}
                    autoFocus
                  />
                  <div className="cc-inline-edit-actions">
                    <button
                      className="cc-btn cc-btn-save"
                      onClick={handleSaveBocEdit}
                      disabled={savingBoc || !bocEditValue.trim()}
                    >
                      {savingBoc ? 'Saving…' : 'Save'}
                    </button>
                    <button className="cc-btn cc-btn-cancel" onClick={() => setEditingBoc(false)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <>
                  {/* Primary / all options */}
                  {!bocExpanded ? (
                    <div className="cc-client-request-content" dir={getDir(displayBocText)}>
                      <p className="cc-section-text" dir={getDir(displayBocText)}>{displayBocText}</p>
                    </div>
                  ) : bocOptions ? (
                    <>
                      <div className="cc-boc-options-list">
                        {bocOptions.map((opt, i) => {
                          const isChecked = bocCheckedIndices.includes(i);
                          const checkOrder = bocCheckedIndices.indexOf(i);
                          return (
                            <div
                              key={i}
                              role="button"
                              tabIndex={0}
                              className={`cc-boc-option${
                                isChecked
                                  ? ' cc-boc-option-checked'
                                  : i === safeSelectedBocIndex && bocCheckedIndices.length === 0
                                  ? ' cc-boc-option-primary'
                                  : ''
                              }`}
                              onClick={() => {
                                if (isStreaming) return;
                                // If any checkboxes are active, card click toggles this checkbox
                                if (bocCheckedIndices.length > 0) {
                                  handleBocCheckboxChange(i);
                                  return;
                                }
                                // Otherwise: immediate single-select + collapse (existing behaviour)
                                setAiSourceMenuOpen(false);
                                setBocExpanded(false);
                                if (i !== safeSelectedBocIndex) handleClearAiSuggestion();
                                setSelectedBocIndex(i);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  if (isStreaming) return;
                                  if (bocCheckedIndices.length > 0) {
                                    handleBocCheckboxChange(i);
                                    return;
                                  }
                                  setAiSourceMenuOpen(false);
                                  setBocExpanded(false);
                                  if (i !== safeSelectedBocIndex) handleClearAiSuggestion();
                                  setSelectedBocIndex(i);
                                }
                              }}
                              title={bocCheckedIndices.length > 0 ? 'Toggle selection' : 'Select this option'}
                            >
                              <div className="cc-boc-option-rank">{opt.rank ?? i + 1}</div>
                              <p className="cc-boc-option-text" dir={getDir(opt.text)}>{opt.text}</p>
                              {/* Checkbox — always visible on the right */}
                              <div className="cc-boc-option-checkbox-wrap">
                                {isChecked && checkOrder >= 0 && (
                                  <span className="cc-boc-check-order">{checkOrder + 1}</span>
                                )}
                                <input
                                  type="checkbox"
                                  className="cc-boc-checkbox"
                                  checked={isChecked}
                                  onChange={e => { e.stopPropagation(); handleBocCheckboxChange(i); }}
                                  onClick={e => e.stopPropagation()}
                                  aria-label={`Select option ${opt.rank ?? i + 1}`}
                                  tabIndex={-1}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      {/* Combined text section — shown when 2+ options are checked */}
                      {bocCheckedIndices.length > 1 && (
                        <div className="cc-boc-combined-section">
                          <div className="cc-boc-combined-header">
                            <span className="cc-boc-combined-label">Combined Text</span>
                            <span className="cc-boc-combined-hint">
                              {bocCheckedIndices.length} clauses — edit as needed
                            </span>
                          </div>
                          <textarea
                            className="cc-edit-textarea cc-boc-combined-textarea"
                            value={bocCombinedText}
                            onChange={e => setBocCombinedText(e.target.value)}
                            dir={getDir(bocCombinedText)}
                            rows={4}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="cc-client-request-content" dir={getDir(displayBocText)}>
                      <p className="cc-section-text" dir={getDir(displayBocText)}>{displayBocText}</p>
                    </div>
                  )}

                  {/* Add new inline form */}
                  {addingBoc && (
                    <div className="cc-inline-edit cc-inline-add">
                      <textarea
                        className="cc-edit-textarea"
                        value={bocNewValue}
                        onChange={e => setBocNewValue(e.target.value)}
                        dir={getDir(bocNewValue)}
                        placeholder="Type new option..."
                        rows={2}
                        autoFocus
                      />
                      <div className="cc-inline-edit-actions">
                        <button
                          className="cc-btn cc-btn-save"
                          onClick={handleAddBoc}
                          disabled={savingBoc || !bocNewValue.trim()}
                        >
                          {savingBoc ? 'Adding…' : 'Add'}
                        </button>
                        <button className="cc-btn cc-btn-cancel" onClick={() => { setAddingBoc(false); setBocNewValue(''); }}>Cancel</button>
                      </div>
                    </div>
                  )}

                  <div className="cc-action-row cc-action-row-boc">
                    {bocCheckedIndices.length === 0 ? (
                      <>
                        <button
                          className="cc-btn cc-btn-add-new"
                          onClick={() => setAddingBoc(true)}
                        >
                          + Add New
                        </button>
                        <button
                          className="cc-btn cc-btn-edit"
                          onClick={() => {
                            setBocEditValue(displayBocText);
                            setEditingBoc(true);
                          }}
                        >
                          <svg className="cc-btn-icon" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                          Edit
                        </button>
                        <button
                          type="button"
                          className="cc-btn cc-btn-submit-as-is"
                          onClick={() => void handleSubmitBankOfChangesAsIs()}
                          disabled={
                            isResponseSubmitted ||
                            isSubmitting ||
                            isUndoing ||
                            isStreaming ||
                            !displayBocText?.trim() ||
                            !clientRequestPlainText?.trim()
                          }
                          title={
                            isResponseSubmitted
                              ? 'Undo response first to submit a different option'
                              : 'Same as Submit to Response: apply this Bank of Changes text to the document and mark as submitted'
                          }
                        >
                          Submit as-is
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          className="cc-btn cc-btn-cancel"
                          onClick={() => { setBocCheckedIndices([]); setBocCombinedText(''); }}
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          className="cc-btn cc-btn-apply-combined"
                          disabled={savingBoc || (bocCheckedIndices.length > 1 && !bocCombinedText.trim())}
                          onClick={() => void handleBocApply()}
                        >
                          {savingBoc
                            ? 'Saving…'
                            : bocCheckedIndices.length > 1
                            ? 'Apply Combined'
                            : 'Apply'}
                        </button>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── PAST AGREEMENT (Construction Loan) ──────────────────── */}
          {pastAgreementOptions && pastAgreementOptions.length > 0 && (
            <div className="cc-section">
              <div className="cc-section-header">
                <div className="cc-boc-header-left">
                  <span className="cc-section-label">Past negotiations</span>
                  {pastAgreementOptions.length > 1 && (
                    <button
                      type="button"
                      className="cc-boc-badge"
                      onClick={() => setPastAgreementExpanded(!pastAgreementExpanded)}
                      title={
                        pastAgreementExpanded
                          ? 'Collapse list'
                          : `Show all ${Math.min(pastAgreementOptions.length, 5)} past negotiations`
                      }
                    >
                      {Math.min(pastAgreementOptions.length, 5)}
                    </button>
                  )}
                </div>
                <button
                  className="cc-chevron-btn"
                  onClick={() => setPastAgreementExpanded(!pastAgreementExpanded)}
                >
                  <svg
                    className={`cc-chevron-icon${pastAgreementExpanded ? ' cc-open' : ''}`}
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>

              {!pastAgreementExpanded ? (
                <div className="cc-pa-collapsed-block">
                  {selectedPastAgreementOption && (
                    <>
                      {(selectedPastAgreementOption.explanation?.trim() ||
                        selectedPastAgreementOption.id ||
                        selectedPastAgreementOption.similarity != null ||
                        isPastAgreementOptionFinalVersion(selectedPastAgreementOption)) && (
                        <div className="cc-pa-badge-row">
                          {isPastAgreementOptionFinalVersion(selectedPastAgreementOption) && (
                            <span className="cc-pa-final-version-badge">Final version</span>
                          )}
                          {selectedPastAgreementOption.explanation?.trim() && (
                            <button
                              type="button"
                              className="cc-pa-explanation-badge"
                              onClick={() =>
                                setPastAgreementExplanationModal({
                                  text: selectedPastAgreementOption.explanation!.trim(),
                                  rank: safeSelectedPastAgreementIndex + 1,
                                })
                              }
                            >
                              Explanation
                            </button>
                          )}
                          {selectedPastAgreementOption.id && (
                            <button
                              type="button"
                              className="cc-pa-source-badge"
                              onClick={() =>
                                setPastAgreementSourceModal({
                                  rank: safeSelectedPastAgreementIndex + 1,
                                  playbookId: selectedPastAgreementOption.id,
                                  searchText: getPastAgreementPlainText(
                                    selectedPastAgreementOption.id,
                                    selectedPastAgreementOption.text,
                                  ),
                                })
                              }
                            >
                              <svg
                                className="cc-pa-source-badge-icon"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Source
                            </button>
                          )}
                          {selectedPastAgreementOption.similarity != null &&
                            !Number.isNaN(selectedPastAgreementOption.similarity) && (
                            <span className="cc-pa-similarity-badge">
                              Similarity: {selectedPastAgreementOption.similarity}%
                            </span>
                          )}
                        </div>
                      )}
                      <div className="cc-client-request-content" dir={selectedPastAgreementDir}>
                        {selectedPastAgreementHtml ? (
                          <div
                            className={`cc-paragraph-xml-display${
                              selectedPastAgreementSide ? ` cc-side-${selectedPastAgreementSide}` : ''
                            }`}
                            dir={selectedPastAgreementDir}
                          >
                            <div
                              className="cc-selected-text-xml"
                              dangerouslySetInnerHTML={{ __html: selectedPastAgreementHtml }}
                              dir={selectedPastAgreementDir}
                            />
                          </div>
                        ) : (() => {
                            const collapsedPlainText = selectedPastAgreementOption.id
                              ? getPastAgreementPlainText(selectedPastAgreementOption.id, selectedPastAgreementOption.text)
                              : (selectedPastAgreementOption.text ?? '');
                            return collapsedPlainText ? (
                              <p className="cc-section-text" dir={getDir(collapsedPlainText)}>{collapsedPlainText}</p>
                            ) : (
                              <p className="cc-section-text cc-empty">
                                XML not available for this playbook record.
                              </p>
                            );
                          })()}
                        {isPastAgreementXmlLoading && (
                          <p className="cc-section-text cc-empty">Loading playbook XML…</p>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="cc-boc-options-list">
                  {pastAgreementOptions.slice(0, 5).map((opt, i) => (
                    (() => {
                      const optionXml = opt.id ? (pastAgreementXmlById[opt.id] || null) : null;
                      const optionSide = opt.id ? (pastAgreementSideById[opt.id] || null) : null;
                      const optionPlainText = opt.id ? getPastAgreementPlainText(opt.id, opt.text) : (opt.text ?? '');
                      let optionHtml: string | null = null;
                      let optionDir: 'rtl' | 'ltr' = getDir(optionPlainText) as 'rtl' | 'ltr';
                      if (optionXml) {
                        try {
                          optionHtml = TrackChangesParser.convertOoxmlToHtml(optionXml);
                          optionDir = getDir(TrackChangesParser.extractText(optionXml)) as 'rtl' | 'ltr';
                        } catch {
                          optionHtml = null;
                        }
                      }
                      return (
                    <div
                      key={opt.id || i}
                      role="button"
                      tabIndex={0}
                      className={`cc-boc-option cc-pa-option-readonly${
                        i === safeSelectedPastAgreementIndex ? ' cc-boc-option-primary' : ''
                      }${isStreaming ? ' cc-pa-option-disabled' : ''}`}
                      onClick={() => {
                        if (isStreaming) return;
                        setAiSourceMenuOpen(false);
                        setPastAgreementExpanded(false);
                        if (i !== safeSelectedPastAgreementIndex) {
                          handleClearAiSuggestion();
                        }
                        setSelectedPastAgreementIndex(i);
                      }}
                      onKeyDown={e => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          if (isStreaming) return;
                          setAiSourceMenuOpen(false);
                          setPastAgreementExpanded(false);
                          if (i !== safeSelectedPastAgreementIndex) {
                            handleClearAiSuggestion();
                          }
                          setSelectedPastAgreementIndex(i);
                        }
                      }}
                      title="Select this past negotiation"
                    >
                      <div className="cc-boc-option-rank">{i + 1}</div>
                      {(opt.explanation?.trim() || opt.id || opt.similarity != null || isPastAgreementOptionFinalVersion(opt)) && (
                        <div className="cc-pa-option-main">
                          <div className="cc-pa-badge-row">
                            {isPastAgreementOptionFinalVersion(opt) && (
                              <span className="cc-pa-final-version-badge">Final version</span>
                            )}
                            {opt.explanation?.trim() && (
                              <button
                                type="button"
                                className="cc-pa-explanation-badge"
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setPastAgreementExplanationModal({
                                    text: opt.explanation!.trim(),
                                    rank: i + 1,
                                  });
                                }}
                              >
                                Explanation
                              </button>
                            )}
                            {opt.id && (
                              <button
                                type="button"
                                className="cc-pa-source-badge"
                                onClick={e => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  setPastAgreementSourceModal({
                                    rank: i + 1,
                                    playbookId: opt.id,
                                    searchText: getPastAgreementPlainText(opt.id, opt.text),
                                  });
                                }}
                              >
                                <svg
                                  className="cc-pa-source-badge-icon"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                Source
                              </button>
                            )}
                            {opt.similarity != null && !Number.isNaN(opt.similarity) && (
                              <span className="cc-pa-similarity-badge">
                                Similarity: {opt.similarity}%
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {optionHtml ? (
                        <div
                          className={`cc-paragraph-xml-display${
                            optionSide ? ` cc-side-${optionSide}` : ''
                          }`}
                          dir={optionDir}
                        >
                          <div
                            className="cc-selected-text-xml"
                            dangerouslySetInnerHTML={{ __html: optionHtml }}
                            dir={optionDir}
                          />
                        </div>
                      ) : optionPlainText ? (
                        <p className="cc-boc-option-text" dir={optionDir}>{optionPlainText}</p>
                      ) : (
                        <p className="cc-boc-option-text cc-empty">
                          XML not available for this playbook record.
                        </p>
                      )}
                    </div>
                      );
                    })()
                  ))}
                </div>
              )}

              <div className="cc-action-row">
                <button
                  type="button"
                  className="cc-btn cc-btn-submit-as-is"
                  onClick={() => void handleSubmitPastNegotiationAsIs()}
                  disabled={
                    isResponseSubmitted ||
                    isSubmitting ||
                    isUndoing ||
                    isStreaming ||
                    !displayPastAgreementText?.trim() ||
                    !clientRequestPlainText?.trim()
                  }
                  title={
                    isResponseSubmitted
                      ? 'Undo response first to submit a different option'
                      : 'Same as Submit to Response: apply this past negotiation text to the document and mark as submitted'
                  }
                >
                  Submit as-is
                </button>
              </div>
            </div>
          )}

          {/* ── SALES AGREEMENT ─────────────────────────────────────── */}
          {clause.sales_agreement && (
            <div className="cc-section">
              {/* Header: title (expand) on the left; trailing group = "Go to" then chevron (expand). */}
              <div className="cc-sa-section-header">
                <button
                  type="button"
                  className="cc-sa-title-toggle"
                  onClick={() => setSalesExpanded(!salesExpanded)}
                  aria-expanded={salesExpanded}
                >
                  <span className="cc-section-label cc-sa-label">Sales Agreement</span>
                </button>
                <div className="cc-sa-header-trailing">
                  {onGoToSalesAgreementSource && (
                    <SalesAgreementGoToBadge
                      onClick={() => onGoToSalesAgreementSource(clause)}
                      disabled={isSalesAgreementSourceLoading}
                      isActive={isSalesAgreementSourceActive}
                      isLoading={isSalesAgreementSourceLoading}
                    />
                  )}
                  <button
                    type="button"
                    className="cc-chevron-btn cc-sa-chevron-btn"
                    onClick={() => setSalesExpanded(!salesExpanded)}
                    aria-expanded={salesExpanded}
                    aria-label={salesExpanded ? 'Collapse Sales Agreement' : 'Expand Sales Agreement'}
                  >
                    <svg
                      className={`cc-chevron-icon${salesExpanded ? ' cc-open' : ''}`}
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              {salesExpanded ? (
                <div className="cc-collapsible-content" dir={salesAgreementDir}>
                  <p className="cc-section-text cc-sa-content" dir={salesAgreementDir}>
                    {clause.sales_agreement.replace(/\s+/g, ' ').trim()}
                  </p>
                </div>
              ) : (
                <p className="cc-sa-preview" dir={salesAgreementDir}>
                  {clause.sales_agreement.replace(/\s+/g, ' ').trim().slice(0, 60)}...
                </p>
              )}
            </div>
          )}

        </div>
        )}
      </div>

      {/* ── PAST AGREEMENT: per-option explanation (Construction Loan) ───────── */}
      {pastAgreementExplanationModal && (
        <div
          className="cc-modal-overlay"
          onClick={() => setPastAgreementExplanationModal(null)}
        >
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <span className="cc-modal-title">
                Past negotiations — Explanation (option {pastAgreementExplanationModal.rank})
              </span>
              <button
                type="button"
                className="cc-modal-close"
                onClick={() => setPastAgreementExplanationModal(null)}
              >
                ✕
              </button>
            </div>
            <div className="cc-modal-body">
              <p
                className="cc-modal-explanation-text"
                dir={getDir(pastAgreementExplanationModal.text)}
              >
                {pastAgreementExplanationModal.text}
              </p>
            </div>
          </div>
        </div>
      )}

      {pastAgreementSourceModal && resolvedProjectId && (
        <PastNegotiationSourceModal
          filename={getPastAgreementSourceDisplay(pastAgreementSourceModal.playbookId)}
          filenameDir={getDir(getPastAgreementSourceDisplay(pastAgreementSourceModal.playbookId))}
          projectId={resolvedProjectId}
          playbookId={pastAgreementSourceModal.playbookId}
          searchText={pastAgreementSourceModal.searchText}
          domain={pastAgreementDomain}
          onClose={() => setPastAgreementSourceModal(null)}
        />
      )}

      {/* ── EXPLANATION MODAL (Track Changes Interpretation; matches dashboard) ── */}
      {showExplanationModal && (clause.track_changes_interpretation ?? primaryInsight?.explanation) && (
        <div className="cc-modal-overlay" onClick={() => setShowExplanationModal(false)}>
          <div className="cc-modal" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header">
              <span className="cc-modal-title">Track Changes Interpretation</span>
              <button className="cc-modal-close" onClick={() => setShowExplanationModal(false)}>✕</button>
            </div>
            <div className="cc-modal-body">
              <p className="cc-modal-explanation-text" dir={getDir(clause.track_changes_interpretation ?? primaryInsight?.explanation ?? '')}>
                {clause.track_changes_interpretation ?? primaryInsight?.explanation ?? ''}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ── Share to BrainShare (after BoC save) ── */}
      {showBrainShareModal && (
        <div
          className="cc-modal-overlay cc-modal-overlay--confirm"
          onClick={sharingBrainShare ? undefined : handleBrainShareCancel}
        >
          <div
            className="cc-modal cc-modal--confirm cc-modal--brainshare"
            role="dialog"
            aria-labelledby="cc-brainshare-title"
            aria-describedby="cc-brainshare-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-modal-header cc-modal-header--brainshare">
              <div className="cc-modal-header-title-row">
                <svg className="cc-brainshare-icon" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <circle cx="18" cy="5" r="3" stroke="currentColor" strokeWidth="2" />
                  <circle cx="6" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  <circle cx="18" cy="19" r="3" stroke="currentColor" strokeWidth="2" />
                  <path d="M8.59 13.51l6.83 3.98M15.41 6.51l-6.82 3.98" stroke="currentColor" strokeWidth="2" />
                </svg>
                <span className="cc-modal-title" id="cc-brainshare-title">
                  Share to BrainShare
                </span>
              </div>
              <button
                type="button"
                className="cc-modal-close"
                onClick={handleBrainShareCancel}
                disabled={sharingBrainShare}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="cc-modal-body cc-modal-body--confirm">
              <p className="cc-confirm-message" id="cc-brainshare-desc">
                Would you like to share this bank of changes entry to BrainShare?
              </p>
              <p className="cc-brainshare-subtext">
                BrainShare allows you to share knowledge across projects. Your supervisor can review
                and approve it for use in other projects.
              </p>
              <div className="cc-confirm-actions cc-confirm-actions--brainshare">
                <button
                  type="button"
                  className="cc-btn cc-btn-cancel"
                  onClick={handleBrainShareCancel}
                  disabled={sharingBrainShare}
                >
                  No, Thanks
                </button>
                <button
                  type="button"
                  className="cc-btn cc-btn-save cc-btn-brainshare"
                  onClick={handleBrainShareConfirm}
                  disabled={sharingBrainShare}
                >
                  {sharingBrainShare ? 'Sharing…' : 'Share to BrainShare'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── View existing clause in Sales Agreement? (AI - Already Exist) ── */}
      {showViewInAgreementConfirm && (
        <div
          className="cc-modal-overlay cc-modal-overlay--confirm"
          onClick={() => handleViewInAgreementConfirmChoice(false)}
        >
          <div
            className="cc-modal cc-modal--confirm"
            role="dialog"
            aria-labelledby="cc-view-in-agreement-title"
            aria-describedby="cc-view-in-agreement-desc"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-modal-header">
              <span className="cc-modal-title" id="cc-view-in-agreement-title">
                View in Sales Agreement?
              </span>
            </div>
            <div className="cc-modal-body cc-modal-body--confirm">
              <p className="cc-confirm-message" id="cc-view-in-agreement-desc">
                An existing clause was detected in the Sales Agreement. Would you like to open it
                in the agreement viewer?
              </p>
              <div className="cc-confirm-actions">
                <button
                  type="button"
                  className="cc-btn cc-btn-cancel"
                  onClick={() => handleViewInAgreementConfirmChoice(false)}
                >
                  Continue without viewing
                </button>
                <button
                  type="button"
                  className="cc-btn cc-btn-save"
                  onClick={() => handleViewInAgreementConfirmChoice(true)}
                >
                  View in agreement
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INSIGHTS MODAL (dashboard "Already Existing Clauses" design) ── */}
      {showInsightModal && (
        <div className="cc-modal-overlay" onClick={closeInsightModal}>
          <div className="cc-modal cc-modal--insights" onClick={e => e.stopPropagation()}>
            <div className="cc-modal-header cc-modal-header--insights">
              <div className="cc-modal-header-title-row">
                <InsightsLightbulbIcon className="cc-insights-header-icon" />
                <span className="cc-modal-title">Already Existing Clauses</span>
              </div>
              <button type="button" className="cc-modal-close cc-modal-close--insights" onClick={closeInsightModal}>
                ×
              </button>
            </div>
            <div className="cc-modal-body cc-modal-body--insights">
              <p className="cc-insights-intro">
                The following clauses already exist in the Sales Agreement and may already fulfill this client request.
                <span className="cc-insights-intro-hint">💡 Click on any clause to scroll to it in the document.</span>
              </p>
              <div className="cc-insights-list">
              {(clause.insights ?? []).map((insight, i) => {
                const aiSuggestion = insightAiSuggestions?.[i];
                const isGeneratingThis = isLoadingInsightAi && !aiSuggestion?.suggestedResponse;
                return (
                <div key={i} className="cc-insight-card">
                  <div className="cc-insight-card-main">
                    <div className="cc-insight-card-row">
                      <span className="cc-insight-number">{i + 1}</span>
                      <div className="cc-insight-card-content">
                        <div className="cc-insight-label-row">
                          <h4 className="cc-insight-label">Existing Clause:</h4>
                          {insight.similarity_score !== undefined && (
                            <span className="cc-insight-similarity-badge">
                              {(insight.similarity_score * 100).toFixed(0)}% Similar
                            </span>
                          )}
                          <div className="cc-modal-insight-section-actions">
                          {canShowInsightGoTo(insight) && (
                            <SalesAgreementGoToBadge
                              className="cc-insight-go-to-badge"
                              onClick={() => handleInsightGoTo(insight)}
                              isActive={isSalesAgreementSourceActive}
                              isLoading={isSalesAgreementSourceLoading}
                            />
                          )}
                          </div>
                        </div>
                        {insight.exist_clause && (
                          <div className="cc-insight-clause-box" dir={getDir(insight.exist_clause)}>
                            {insight.exist_clause}
                          </div>
                        )}
                        {insight.explanation && (
                          <div className="cc-insight-explanation">
                            <h4 className="cc-insight-label">Explanation:</h4>
                            <p dir={getDir(insight.explanation)}>{insight.explanation}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {(insight.suggested_xml || isLoadingInsightAi || aiSuggestion) && (
                    <div className="cc-insight-ai-section" onClick={e => e.stopPropagation()}>
                      <div className="cc-insight-ai-header">
                        {(isGeneratingThis || isLoadingInsightAi) && (
                          <span className="cc-insight-ai-spinner" aria-hidden />
                        )}
                        {!isGeneratingThis && !isLoadingInsightAi && (
                          <InsightsLightbulbIcon className="cc-insight-ai-icon" />
                        )}
                        <h4 className="cc-insight-ai-title">
                          AI Suggested Response{(isGeneratingThis || (isLoadingInsightAi && !aiSuggestion?.suggestedResponse)) ? ' (generating...)' : ''}:
                        </h4>
                      </div>
                      <div className="cc-insight-ai-box">
                        {insight.suggested_xml ? (
                          <ClientRequestXmlPreview xml={insight.suggested_xml} dir={getDir(insight.suggested_xml)} />
                        ) : isGeneratingThis ? (
                          <div className="cc-insight-ai-generating">
                            <span className="cc-insight-ai-spinner" aria-hidden />
                            Generating AI suggestion...
                          </div>
                        ) : aiSuggestion?.suggestedResponse ? (
                          <>
                            <InsightSuggestionPreview
                              suggestedText={aiSuggestion.suggestedResponse}
                              clientRequestXml={clientRequestXml}
                              clientRequestPlainText={clientRequestPlainText}
                            />
                            <div className="cc-insight-ai-actions">
                              <button
                                type="button"
                                className="cc-insight-apply-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleApplyInsightSuggestion(aiSuggestion.suggestedResponse);
                                }}
                              >
                                Apply This Response
                              </button>
                            </div>
                          </>
                        ) : (
                          <p className="cc-modal-empty">No suggestion generated.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                );
              })}
              {(!clause.insights || clause.insights.length === 0) && (
                <p className="cc-modal-empty">No insights available.</p>
              )}
              </div>
            </div>
            <div className="cc-modal-footer cc-modal-footer--insights">
              <button
                type="button"
                className="cc-insights-generate-btn"
                onClick={() => void handleGenerateInsightAiSuggestions()}
                disabled={isLoadingInsightAi}
              >
                {isLoadingInsightAi ? (
                  <>
                    <span className="cc-insight-ai-spinner cc-insight-ai-spinner--white" aria-hidden />
                    Generating...
                  </>
                ) : (
                  <>
                    <InsightsLightbulbIcon className="cc-insights-generate-icon" />
                    AI Suggestions
                  </>
                )}
              </button>
              <button type="button" className="cc-insights-close-btn" onClick={closeInsightModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </>
  );
};

function normalizeClauseMatter(matter?: string): CrossCheckMatter {
  const m = (matter || 'other').trim().toLowerCase();
  if (m === 'commercial' || m === 'legal' || m === 'other') {
    return m;
  }
  return 'other';
}

interface ClauseCardKebabMenuProps {
  clause: CrossCheckClause;
  kebabVariant: 'mcc' | 'construction-loan';
  isMutating: boolean;
  isLocked: boolean;
  isClauseSubmitted: boolean;
  moveBlockedMessage: string;
  isMenuOpen: boolean;
  isMoveSubmenuOpen: boolean;
  moveSubmenuPosition: { top: number; left: number } | null;
  menuRef: React.Ref<HTMLDivElement>;
  moveMenuItemRef: React.Ref<HTMLButtonElement>;
  moveSubmenuRef: React.Ref<HTMLDivElement>;
  onMoveClause?: (
    clause: CrossCheckClause,
    target: 'sales_agreement' | 'appendix_a' | 'saved_for_later',
  ) => void;
  onMoveClauseMatter?: (clause: CrossCheckClause, target: CrossCheckMatter) => void;
  onAnalyzeAgain?: (clause: CrossCheckClause) => void;
  onAskAgent?: (clause: CrossCheckClause) => void;
  onToggleMenu: () => void;
  onToggleMoveSubmenu: () => void;
  onCloseMenu: () => void;
}

const ClauseCardKebabMenu: React.FC<ClauseCardKebabMenuProps> = ({
  clause,
  kebabVariant,
  isMutating,
  isLocked,
  isClauseSubmitted,
  moveBlockedMessage,
  isMenuOpen,
  isMoveSubmenuOpen,
  moveSubmenuPosition,
  menuRef,
  moveMenuItemRef,
  moveSubmenuRef,
  onMoveClause,
  onMoveClauseMatter,
  onAnalyzeAgain,
  onAskAgent,
  onToggleMenu,
  onToggleMoveSubmenu,
  onCloseMenu,
}) => {
  const isClKebab = kebabVariant === 'construction-loan';
  const currentMatter = normalizeClauseMatter(clause.matter);
  const showMove = isClKebab ? Boolean(onMoveClauseMatter) : Boolean(onMoveClause);
  const showAnalyzeAgain =
    Boolean(onAnalyzeAgain) &&
    (isClKebab ||
      clause.documentType === 'sales_agreement' ||
      clause.documentType === 'appendix_a');

  return (
  <div className="cc-kebab-wrap" ref={menuRef}>
    <button
      type="button"
      className={`cc-action-btn cc-kebab-btn${isMutating || isLocked ? ' cc-kebab-disabled' : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        onToggleMenu();
      }}
      disabled={isMutating || isLocked}
      title={isLocked ? 'Unlock to access actions' : 'More actions'}
      aria-haspopup="menu"
      aria-expanded={isMenuOpen}
    >
      {isMutating ? (
        <span className="cc-spinner-small" aria-hidden />
      ) : (
        <MoreVertical className="cc-icon" aria-hidden />
      )}
    </button>

    {isMenuOpen && (
      <div className="cc-kebab-menu" role="menu" onClick={(e) => e.stopPropagation()}>
        {showMove && (
          <div className="cc-kebab-move-wrap">
            <button
              ref={moveMenuItemRef}
              type="button"
              role="menuitem"
              disabled={isClauseSubmitted}
              title={isClauseSubmitted ? moveBlockedMessage : undefined}
              className={`cc-kebab-item${isClauseSubmitted ? ' cc-kebab-item-disabled' : ''}`}
              onClick={onToggleMoveSubmenu}
            >
              <span>Move</span>
              <svg className="cc-kebab-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
            {isMoveSubmenuOpen && !isClauseSubmitted && moveSubmenuPosition && createPortal(
              <div
                ref={moveSubmenuRef}
                className="cc-kebab-submenu"
                style={{
                  top: moveSubmenuPosition.top,
                  left: moveSubmenuPosition.left,
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {(isClKebab
                  ? ([
                      { key: 'commercial' as const, label: 'Commercial' },
                      { key: 'legal' as const, label: 'Legal' },
                      { key: 'other' as const, label: 'Other' },
                    ] as const)
                  : ([
                      { key: 'sales_agreement' as const, label: 'Sales Agreement' },
                      { key: 'appendix_a' as const, label: 'Appendix A' },
                      { key: 'saved_for_later' as const, label: 'Saved' },
                    ] as const)
                ).map((target) => {
                  const isCurrent = isClKebab
                    ? currentMatter === target.key
                    : clause.documentType === target.key;
                  return (
                    <button
                      key={target.key}
                      type="button"
                      disabled={isCurrent}
                      className={`cc-kebab-item${isCurrent ? ' cc-kebab-item-disabled' : ''}`}
                      onClick={() => {
                        onCloseMenu();
                        if (!isCurrent) {
                          if (isClKebab && onMoveClauseMatter) {
                            onMoveClauseMatter(clause, target.key as CrossCheckMatter);
                          } else if (!isClKebab && onMoveClause) {
                            onMoveClause(
                              clause,
                              target.key as 'sales_agreement' | 'appendix_a' | 'saved_for_later',
                            );
                          }
                        }
                      }}
                    >
                      <span>{target.label}</span>
                      {isCurrent && <span className="cc-kebab-current">current</span>}
                    </button>
                  );
                })}
              </div>,
              document.body,
            )}
          </div>
        )}
        {showAnalyzeAgain && (
          <button
            type="button"
            role="menuitem"
            className="cc-kebab-item"
            onClick={() => {
              onCloseMenu();
              onAnalyzeAgain?.(clause);
            }}
          >
            Analyze again
          </button>
        )}
        {onAskAgent && (
          <button
            type="button"
            role="menuitem"
            className="cc-kebab-item"
            onClick={() => {
              onCloseMenu();
              onAskAgent(clause);
            }}
          >
            Ask Agent
          </button>
        )}
      </div>
    )}
  </div>
  );
};

export default CrossCheckClauseCard;
